import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Text } from "../components/catalyst/text";
import { Checkbox } from "../components/ui/checkbox";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from "../components/ui/card";
import { Header } from "../layout/Header";
import {
  MapPin,
  Bookmark,
  Search,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  X,
  CalendarIcon,
  Map,
  Target,
  Building2,
  MessagesSquare,
  MessageSquare,
  CheckSquare,
  Plus,
  ListChecks,
  Info,
  BarChart3,
  Newspaper,
  Users,
  Mail,
  Phone,
  Loader2,
  DollarSign,
  Package,
  Heart,
  FileText,
  Edit,
  Projector,
  UserPlus,
  Check,
  UserMinus,
  Globe,
  CheckCircle2,
  ClipboardCheck,
} from "lucide-react";
import { ScrollArea } from "../components/ui/scroll-area";
import { format, parse, startOfWeek, getDay, addDays, isValid } from "date-fns";
import enUS from "date-fns/locale/en-US";
import { Calendar as BigCalendar, dateFnsLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import MapSection from "../components/MapSection";
import { Progress } from "../components/ui/progress";
import { formatDate } from "../utils/dateUtils";
import AssignRsmModal from "../components/AssignRsmModal";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "../components/ui/use-toast";
import { cn } from "../utils/cn";
import * as ics from "ics"; // Import ics library
import PrioritizedDataCard from "../components/PrioritizedDataCard";
import ChurnMetricsCard from "../components/ChurnMetricsCard";
import QORCMetricsCard from "../components/QORCMetricsCard";
import useChurnData from "../hooks/useChurnData";
import { useHubSpot } from "../hooks/useHubSpot";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "../components/ui/dialog";
import { Separator } from "../components/ui/separator";
import { marked } from "marked";
import CustomerEngagementHistory from "../components/CustomerEngagementHistory";
import {
  getCustomerDetails,
  getCustomerOpportunities,
} from "../services/v2ApiService";
import { MapContainer, TileLayer, Marker, Popup as LeafletPopup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Custom CSS to fix calendar background inconsistencies
const calendarFixStyles = `
  /* Ensure all day slots have grid lines and consistent styling */
  .calendar-fix .rbc-time-view {
    border: 1px solid var(--border);
  }

  .calendar-fix .rbc-day-slot {
    background: var(--background);
    position: relative;
    border-right: 1px solid var(--border);
  }

  .calendar-fix .rbc-time-content {
    border-top: 1px solid var(--border);
  }

  .calendar-fix .rbc-timeslot-group {
    border-bottom: 1px solid var(--border);
  }

  /* Fix specifically for Thursday rendering issue */
  .calendar-fix .rbc-time-header-content:nth-child(5) .rbc-header {
    border-bottom: 1px solid var(--border);
  }

  .calendar-fix .rbc-time-header-content:nth-child(5) ~ .rbc-time-content .rbc-day-slot {
    background: var(--background);
    border-right: 1px solid var(--border);
  }
`;

// Calendar localizer setup
const locales = {
  "en-US": enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
  formats: {
    monthHeaderFormat: "MMMM yyyy",
    dayHeaderFormat: "EEE M/d",
    weekHeaderFormat: "MMMM dd",
    dayRangeHeaderFormat: "MMMM dd",
    timeGutterFormat: "h:mm a",
    eventTimeRangeFormat: "h:mm a",
    selectRangeFormat: "h:mm a",
    agendaHeaderFormat: "MMMM dd",
    agendaDateFormat: "EEE M/d",
    agendaTimeFormat: "h:mm a",
    agendaTimeRangeFormat: "h:mm a",
  },
});

// --- Calendar Formats ---
const calendarFormats = {
  monthHeaderFormat: "MMMM yyyy",
  dayHeaderFormat: "EEE M/d",
  weekHeaderFormat: "MMMM dd",
  dayRangeHeaderFormat: ({ start, end }, culture, localizer) =>
    localizer.format(start, "MMMM dd", culture) +
    " - " +
    localizer.format(end, "MMMM dd", culture),
  timeGutterFormat: "h:mm a",
  eventTimeRangeFormat: ({ start, end }, culture, localizer) =>
    localizer.format(start, "h:mm a", culture) +
    " - " +
    localizer.format(end, "h:mm a", culture),
  selectRangeFormat: ({ start, end }, culture, localizer) =>
    localizer.format(start, "h:mm a", culture) +
    " - " +
    localizer.format(end, "h:mm a", culture),
  agendaHeaderFormat: ({ start, end }, culture, localizer) =>
    localizer.format(start, "MMMM dd", culture) +
    " - " +
    localizer.format(end, "MMMM dd", culture),
  agendaDateFormat: "EEE M/d",
  agendaTimeFormat: "h:mm a",
  agendaTimeRangeFormat: ({ start, end }, culture, localizer) =>
    localizer.format(start, "h:mm a", culture) +
    " - " +
    localizer.format(end, "h:mm a", culture),
};

// Create Drag and Drop Calendar
const DragAndDropCalendar = withDragAndDrop(BigCalendar);

// SafeCalendar wrapper for error handling
const SafeCalendar = React.forwardRef((props, ref) => {
  try {
    // Ensure localizer and formats are valid before rendering
    if (!props.localizer || !props.localizer.formats) {
      console.error("SafeCalendar: Invalid localizer or missing formats");
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <p className="text-muted-foreground">Calendar unavailable</p>
            <p className="text-xs text-muted-foreground">
              Localizer configuration error
            </p>
          </div>
        </div>
      );
    }
    return <DragAndDropCalendar ref={ref} {...props} />;
  } catch (error) {
    console.error("SafeCalendar: Error rendering calendar:", error);
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-muted-foreground">Calendar error</p>
          <p className="text-xs text-muted-foreground">
            Please refresh the page
          </p>
        </div>
      </div>
    );
  }
});

// Helper to check if a date is valid
const isValidDate = (date) => {
  return date && isValid(date) && !isNaN(date.getTime());
};

// Progress status colors
const statusColors = {
  PENDING: "bg-muted",
  IN_PROGRESS: "bg-blue-500",
  COMPLETED: "bg-green-500",
  CANCELLED: "bg-red-500",
  RESCHEDULED: "bg-amber-500",
};

// Helper function to extract QORC data from customer, used for customer cards
const extractQorcData = (customer) => {
  // Check if we have metrics data from the API
  if (customer.metrics) {
    return {
      ranking: customer.metrics.priorityranking || customer.qorc?.ranking || "",
      q_score:
        parseFloat(customer.metrics.qscore) || customer.qorc?.q_score || 0,
      o_score:
        parseFloat(customer.metrics.oscore) || customer.qorc?.o_score || 0,
      r_score:
        parseFloat(customer.metrics.rscore) || customer.qorc?.r_score || 0,
      c_score:
        parseFloat(customer.metrics.cscore) || customer.qorc?.c_score || 0,
    };
  }

  // Fallback to qorc data if available
  const qorcData = customer.qorc || {};

  // Add fallback values if needed
  return {
    ranking: qorcData.ranking || "",
    q_score: qorcData.q_score || 0,
    o_score: qorcData.o_score || 0,
    r_score: qorcData.r_score || 0,
    c_score: qorcData.c_score || 0,
    ...qorcData,
  };
};

function BuildItineraryPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Role-based permissions
  const isRSM = user?.role === "RSM";
  const isBM = user?.role === "BM";

  // Check if we're in edit mode
  const searchParams = new URLSearchParams(location.search);
  const editItineraryId = searchParams.get("edit");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customersError, setCustomersError] = useState(null);
  const [selectedCustomers, setSelectedCustomers] = useState({});
  const [selectedItems, setSelectedItems] = useState([]); // New state for tracking all selected items (customers and opportunities)
  const [searchQuery, setSearchQuery] = useState("");
  const [filterOptions, setFilterOptions] = useState({});
  const [selectedFilters, setSelectedFilters] = useState({
    regions: [],
    states: [],
    markets: [],
    priority_buckets: [],
    priority_levels: [],
  });

  // Pagination states - increased size to handle large datasets better
  const [currentPage, setCurrentPage] = useState(1);
  const [showAllResults, setShowAllResults] = useState(false);
  const CUSTOMERS_PER_PAGE = 200; // Increased from 50 to show more data per page
  const SHOW_ALL_THRESHOLD = 500; // Show "Show All" option when filtered results are manageable

  // Calendar states
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendarInitialized, setCalendarInitialized] = useState(false);
  const [itinerarySaved, setItinerarySaved] = useState(false);
  const lastScrollY = useRef(0);

  // Enhanced drag and drop states
  const [isDragging, setIsDragging] = useState(false);
  const [draggedEvent, setDraggedEvent] = useState(null);
  const [autoNavTimeout, setAutoNavTimeout] = useState(null);
  const [pendingDrop, setPendingDrop] = useState(null); // For cross-week drag persistence
  const [isAutoNavigating, setIsAutoNavigating] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Opportunity selection states
  const [showOpportunities, setShowOpportunities] = useState(false);
  const [
    selectedCustomerForOpportunities,
    setSelectedCustomerForOpportunities,
  ] = useState(null);
  const [customerOpportunities, setCustomerOpportunities] = useState([]);
  const [selectedOpportunityIds, setSelectedOpportunityIds] = useState([]);
  const [opportunitiesLoading, setOpportunitiesLoading] = useState(false);
  const [opportunitiesError, setOpportunitiesError] = useState(null);
  const [showNullCoordOpportunities, setShowNullCoordOpportunities] =
    useState(true);

  const {
    loading: churnLoading,
    fetchChurnData,
    getChurnDataForCustomer,
  } = useChurnData();
  const [selectedCustomerForDialog, setSelectedCustomerForDialog] =
    useState(null);
  const [customerDetailsData, setCustomerDetailsData] = useState({});
  const [customerOpportunitiesData, setCustomerOpportunitiesData] = useState(
    {},
  );
  const [customerDetailsLoading, setCustomerDetailsLoading] = useState({});
  const [customerOpportunitiesLoading, setCustomerOpportunitiesLoading] =
    useState({});
  const [selectedCustomerOpportunities, setSelectedCustomerOpportunities] =
    useState(null);
  const [opportunitiesDialogOpen, setOpportunitiesDialogOpen] = useState(false);

  // Additional customer opportunities dialog states
  const [opportunitiesFilter, setOpportunitiesFilter] = useState('all'); // all, quotes, orders, rentals
  const [opportunitiesSort, setOpportunitiesSort] = useState('amount_desc'); // amount_desc, amount_asc, name_asc, date_desc
  const [opportunitiesSearch, setOpportunitiesSearch] = useState('');
  const [showOnlyMappable, setShowOnlyMappable] = useState(false); // Filter for opportunities with coordinates
  const [mapPopupOpen, setMapPopupOpen] = useState(false);
  const [selectedMapOpportunity, setSelectedMapOpportunity] = useState(null);

  // Add state for number of days and loading for fill assist
  const [numDays, setNumDays] = useState(5); // Default to 5 days
  const [fillAssistLoading, setFillAssistLoading] = useState(false);

  // Debug output (removed to prevent console spam)
  // console.log("BuildItineraryPage render - User:", user?.username);
  // console.log("BuildItineraryPage - Customers:", customers?.length);
  // console.log("BuildItineraryPage - Loading:", loading);
  // console.log("BuildItineraryPage - Error:", error);

  // Time settings for calendar (min/max times)
  const timeSettings = useMemo(() => {
    const minTime = new Date();
    minTime.setHours(8, 0, 0); // 8:00 AM
    const maxTime = new Date();
    maxTime.setHours(18, 0, 0); // 6:00 PM (18:00)
    return { minTime, maxTime };
  }, []);

  // Calculate scroll time for calendar
  const { scrollToTimeValue } = useMemo(() => {
    const today = new Date();
    today.setHours(9, 0, 0, 0); // Default scroll to 9 AM
    return { scrollToTimeValue: today };
  }, []);

  useEffect(() => {
    fetchCustomers();
    fetchFilterOptions();
  }, []);

  // Load itinerary data if in edit mode
  useEffect(() => {
    if (editItineraryId && customers.length > 0) {
      loadItineraryForEdit();
    }
  }, [editItineraryId, customers.length]);

  // Apply filters and search whenever customers, filters, or search query changes
  useEffect(() => {
    if (customers.length > 0) {
      let filtered = [...customers];

      // Apply search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(
          (customer) =>
            (customer.customername &&
              customer.customername.toLowerCase().includes(query)) ||
            (customer.customercode &&
              customer.customercode.toString().includes(query)),
        );
      }

      // Apply dropdown filters
      if (selectedFilters.regions.length > 0) {
        filtered = filtered.filter((customer) =>
          selectedFilters.regions.includes(customer.salesregion),
        );
      }

      if (selectedFilters.states.length > 0) {
        filtered = filtered.filter((customer) =>
          selectedFilters.states.includes(customer.state),
        );
      }

      if (selectedFilters.markets.length > 0) {
        filtered = filtered.filter((customer) =>
          selectedFilters.markets.includes(customer.market),
        );
      }

      if (selectedFilters.priority_buckets.length > 0) {
        filtered = filtered.filter((customer) =>
          selectedFilters.priority_buckets.includes(customer.prioritybucket),
        );
      }

      // Apply priority level filter
      if (selectedFilters.priority_levels.length > 0) {
        filtered = filtered.filter((customer) => {
          // Check if the priority value (case-insensitive) is in the selected levels
          const customerPriority = (
            customer.prioritybucket || ""
          ).toLowerCase();
          return selectedFilters.priority_levels.some((level) =>
            customerPriority.includes(level.toLowerCase()),
          );
        });
      }

      setFilteredCustomers(filtered);
    } else {
      setFilteredCustomers([]);
    }

    // Reset to first page and pagination mode when filters change
    setCurrentPage(1);
    setShowAllResults(false);
  }, [customers, searchQuery, selectedFilters]);

  // Paginated customers for rendering
  const paginatedCustomers = useMemo(() => {
    if (showAllResults || filteredCustomers.length <= SHOW_ALL_THRESHOLD) {
      return filteredCustomers; // Show all when manageable or requested
    }
    const startIndex = (currentPage - 1) * CUSTOMERS_PER_PAGE;
    const endIndex = startIndex + CUSTOMERS_PER_PAGE;
    return filteredCustomers.slice(startIndex, endIndex);
  }, [
    filteredCustomers,
    currentPage,
    CUSTOMERS_PER_PAGE,
    showAllResults,
    SHOW_ALL_THRESHOLD,
  ]);

  const totalPages = Math.ceil(filteredCustomers.length / CUSTOMERS_PER_PAGE);

  //   // OLD Effect to update calendar events when selected customers change - DISABLED
  //   // useEffect(() => {
  //   //   const selectedCustomerIds = Object.keys(selectedCustomers).filter(id => selectedCustomers[id]);
  //
  //   //   // Reset saved state when making changes
  //   //   setItinerarySaved(false);
  //
  //   //   if (selectedCustomerIds.length === 0) {
  //   //     setCalendarEvents([]);
  //   //     return;
  //   //   }
  //
  //     // Create a map of existing events by customer ID
  //     const existingEventsMap = {};
  //     calendarEvents.forEach(event => {
  //       if (event.id) {
  //         existingEventsMap[event.id] = event;
  //       }
  //     });
  //
  //     // Get the current week's start date (Monday)
  //     const today = new Date();
  //     const currentWeekStart = startOfWeek(today, { weekStartsOn: 1 }); // 1 = Monday
  //
  //     // Convert selected customers to calendar events
  //     const updatedEvents = selectedCustomerIds.map((id, index) => {
  //       const customer = customers.find(c => c._uniqueId === id);
  //       if (!customer) return null;
  //
  //       // If this customer already has an event, preserve its position
  //       if (existingEventsMap[id]) {
  //         return existingEventsMap[id];
  //       }
  //
  //       // This is a new customer, create a new event
  //       // Distribute events across the week - use modulo to repeat days if more than 5 customers
  //       const dayOffset = index % 5; // Monday through Friday (0-4)
  //
  //       // Create event date (current week start + day offset)
  //       const eventDate = new Date(currentWeekStart);
  //       eventDate.setDate(eventDate.getDate() + dayOffset);
  //
  //       // Count how many customers are assigned to this day already
  //       // Only count customers that don't already have events
  //       const newCustomersOnThisDay = selectedCustomerIds
  //         .filter(customerId => !existingEventsMap[customerId]) // Only consider new customers
  //         .slice(0, index)
  //         .filter((_, i) => i % 5 === dayOffset)
  //         .length;
  //
  //       const hour = 9 + newCustomersOnThisDay; // Start at 9 AM, then 10 AM, 11 AM, etc.
  //
  //       // Set start and end times
  //       const startTime = new Date(eventDate);
  //       startTime.setHours(hour, 0, 0, 0);
  //
  //       const endTime = new Date(startTime);
  //       endTime.setHours(startTime.getHours() + 1);
  //
  //       // Use opportunity address if available
  //       let eventResource = { ...customer };
  //       if (customer.selected_opportunities && customer.selected_opportunities.length > 0) {
  //         const firstOpp = customer.selected_opportunities[0];
  //         if (firstOpp.address || firstOpp.coordinates) {
  //           eventResource = {
  //             ...customer,
  //             formatted_address: firstOpp.address || customer.formatted_address,
  //             city: firstOpp.city || customer.city,
  //             state: firstOpp.state || customer.state,
  //             zipcode: firstOpp.zipcode || customer.zipcode,
  //             coordinates: firstOpp.coordinates || customer.coordinates,
  //             // Keep original customer address for reference
  //             original_address: customer.formatted_address
  //           };
  //         }
  //       }
  //
  //       return {
  //         id: id,
  //         title: customer.customername || "Unnamed Customer",
  //         start: startTime,
  //         end: endTime,
  //         resource: eventResource
  //       };
  //     }).filter(Boolean);
  //
  //     // Only update if events actually changed to prevent infinite loops
  //     const eventsChanged = JSON.stringify(updatedEvents.map(e => ({ id: e.id, start: e.start.getTime(), end: e.end.getTime() }))) !==
  //                          JSON.stringify(calendarEvents.map(e => ({ id: e.id, start: e.start.getTime(), end: e.end.getTime() })));
  //
  //     if (eventsChanged) {
  //       setCalendarEvents(updatedEvents);
  //     }
  //
  //     // Initialize calendar to current week if not already initialized
  //     if (!calendarInitialized && updatedEvents.length > 0) {
  //       setCurrentDate(updatedEvents[0].start);
  //       setCalendarInitialized(true);
  //     }
  //   }, [selectedCustomers, customers]); // Removed calendarEvents and calendarInitialized from deps to prevent infinite loop

  // NEW Effect to update calendar events when selected items change
  useEffect(() => {
    // Reset saved state when making changes
    setItinerarySaved(false);

    if (selectedItems.length === 0) {
      setCalendarEvents([]);
      return;
    }

    // Use functional update to get current calendar events
    setCalendarEvents((prevCalendarEvents) => {
      // Create a map of existing events by item ID (using resource ID for mapping)
      const existingEventsMap = {};
      prevCalendarEvents.forEach((event) => {
        if (event.resource && event.resource.id) {
          existingEventsMap[event.resource.id] = event;
        }
      });

    // Get the current week's start date (Monday)
    const today = new Date();
    const currentWeekStart = startOfWeek(today, { weekStartsOn: 1 }); // 1 = Monday

    // Convert selected items to calendar events
    const updatedEvents = selectedItems
      .map((item, index) => {
        // If this item already has an event, preserve its position
        if (existingEventsMap[item.id]) {
          return existingEventsMap[item.id];
        }

        // This is a new item, create a new event
        // Distribute events across the week - use modulo to repeat days if more than 5 items
        const dayOffset = index % 5; // Monday through Friday (0-4)

        // Create event date (current week start + day offset)
        const eventDate = new Date(currentWeekStart);
        eventDate.setDate(eventDate.getDate() + dayOffset);

        // Count how many items are assigned to this day already
        // Only count items that don't already have events
        const newItemsOnThisDay = selectedItems
          .filter((selectedItem) => !existingEventsMap[selectedItem.id]) // Only consider new items
          .slice(0, index)
          .filter((_, i) => i % 5 === dayOffset).length;

        const hour = 9 + newItemsOnThisDay; // Start at 9 AM, then 10 AM, 11 AM, etc.

        // Set start and end times
        const startTime = new Date(eventDate);
        startTime.setHours(hour, 0, 0, 0);

        const endTime = new Date(startTime);
        endTime.setHours(startTime.getHours() + 1);

        return {
          id: `${item.id}-${index}-${Date.now()}`,
          title: item.customername || "Unnamed Customer",
          start: startTime,
          end: endTime,
          resource: item, // Use the item directly as resource
        };
      })
      .filter(Boolean);

      return updatedEvents;
    });

    // Set calendar initialized flag when we have items
    if (selectedItems.length > 0 && !calendarInitialized) {
      setCalendarInitialized(true);
    }
  }, [selectedItems]); // Only depend on selectedItems for calendar updates

  // Separate effect to initialize calendar date when first item is added
  useEffect(() => {
    if (selectedItems.length > 0 && !calendarInitialized) {
      const today = new Date();
      const currentWeekStart = startOfWeek(today, { weekStartsOn: 1 });
      setCurrentDate(currentWeekStart);
    }
  }, [selectedItems.length, calendarInitialized]);

  // Fetch filter options
  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get("/filter_options");
      if (response.data && typeof response.data === "object") {
        setFilterOptions(response.data);
      } else {
        setFilterOptions({});
      }
    } catch (err) {
      console.error("Error fetching filter options:", err);
    }
  };

  // Fetch customers
  const fetchCustomers = async () => {
    setCustomersLoading(true);
    setCustomersError(null);
    try {
      console.log(
        "Fetching customers from:",
        `${axios.defaults.baseURL}/customers/all`,
      );

      const response = await axios.get("/customers/all");

      // Debug: Log comprehensive response details
      console.log("Full API Response Details:", {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
        dataType: typeof response.data,
        dataLength: response.data?.length,
        isArray: Array.isArray(response.data),
        rawData: response.data,
        dataPreview:
          typeof response.data === "string"
            ? response.data.substring(0, 200)
            : response.data,
      });

      // Check for empty response
      if (
        response.data === "" ||
        response.data === null ||
        response.data === undefined
      ) {
        throw new Error(
          "API returned empty response. Check if the backend is running and the endpoint exists.",
        );
      }

      // Check for HTML error page (common when backend is down)
      if (
        typeof response.data === "string" &&
        response.data.includes("<html>")
      ) {
        throw new Error(
          "API returned HTML instead of JSON. Backend may be down or endpoint not found.",
        );
      }

      // Try to parse string response as JSON if needed
      let parsedData = response.data;
      if (
        typeof response.data === "string" &&
        response.data.trim().length > 0
      ) {
        try {
          parsedData = JSON.parse(response.data);
          console.log(
            "Successfully parsed string response as JSON:",
            parsedData,
          );
        } catch (parseError) {
          throw new Error(
            `API returned invalid JSON string: "${response.data.substring(0, 100)}..."`,
          );
        }
      }

      // Validate response format - should be a direct array of customer objects
      if (!Array.isArray(parsedData)) {
        throw new Error(
          `Invalid response format: expected array, got ${typeof parsedData}. Data: ${JSON.stringify(parsedData)}`,
        );
      }

      const customersData = parsedData;

      if (customersData.length === 0) {
        console.warn("No customers returned from API");
        setCustomers([]);
        setFilteredCustomers([]);
        return;
      }

      console.log(`Processing ${customersData.length} customers from API`);

      // Process ALL customers - no data truncation!

      // Add unique identifiers to each customer and format location
      const customersWithIds = customersData
        .map((customer, index) => {
          // Create a formatted address for display
          const address = [
            customer.streetaddress1 || customer.street_address,
            customer.city,
            customer.state,
            customer.zip || customer.zipcode,
          ]
            .filter(Boolean)
            .join(", ");

          // Skip processing if customer data is malformed (memory protection)
          if (!customer || typeof customer !== "object") {
            console.warn(
              `Malformed customer data at index ${index}:`,
              customer,
            );
            return null;
          }

          // Validate required fields
          if (!customer.customercode) {
            console.warn(
              `Customer at index ${index} missing customercode:`,
              customer,
            );
            return null;
          }

          // Check if customer has valid coordinates (expecting lat/lng numbers)
          const hasValidCoordinates =
            customer.lat &&
            customer.lng &&
            !isNaN(parseFloat(customer.lat)) &&
            !isNaN(parseFloat(customer.lng));

          // Get the coordinate values for validation
          let coordLat, coordLng;
          if (hasValidCoordinates) {
            coordLat = parseFloat(customer.lat);
            coordLng = parseFloat(customer.lng);
          } else {
            console.warn(
              `Customer ${customer.customername} (${customer.customercode}) has invalid coordinates:`,
              {
                lat: customer.lat,
                lng: customer.lng,
              },
            );
            return null;
          }

          // Validate coordinates make geographical sense for the customer's state
          const hasGeographicallyValidCoords =
            hasValidCoordinates &&
            coordLat &&
            coordLng &&
            (() => {
              // California should be roughly: lat 32-42, lng -124 to -114
              if (customer.state === "CA") {
                return (
                  coordLat >= 32 &&
                  coordLat <= 42 &&
                  coordLng >= -124 &&
                  coordLng <= -114
                );
              }

              // For now, just check coordinates are within valid Earth ranges
              // You can add more state-specific validations as needed
              return (
                coordLat >= -90 &&
                coordLat <= 90 &&
                coordLng >= -180 &&
                coordLng <= 180
              );
            })();

          // Log customers with bad coordinates
          if (hasValidCoordinates && !hasGeographicallyValidCoords) {
            console.warn(
              `Customer ${customer.customername} in ${customer.city}, ${customer.state} has coordinates that don't match location:`,
              {
                customer: customer.customername,
                city: customer.city,
                state: customer.state,
                coordinates: [coordLat, coordLng],
              },
            );
          }

          // Debug log to see what coordinate fields are available (remove after testing)
          if (index < 5) {
            // Check first 5 customers to see coordinate data
            console.log("Customer coordinate analysis:", {
              customername: customer.customername,
              city: customer.city,
              state: customer.state,
              zip: customer.zip,
              coordinates: customer.coordinates,
              latitude: customer.latitude,
              longitude: customer.longitude,
              lat: customer.lat,
              lng: customer.lng,
              hasValidCoordinates,
              // Show all customer fields to identify coordinate fields
              allFields: Object.keys(customer).filter(
                (key) =>
                  key.toLowerCase().includes("lat") ||
                  key.toLowerCase().includes("lng") ||
                  key.toLowerCase().includes("coord"),
              ),
            });
          }

          // Create an enhanced customer object with placeholder fields for detailed data
          return {
            ...customer,
            _uniqueId: `cust_${index}_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
            formatted_address: address,
            // Use the location field from API, or generate from lat/lng
            location: customer.location || `${coordLat},${coordLng}`,
            // Initialize empty arrays for data that will be fetched later
            open_quotes: [],
            open_orders: [],
            active_rentals: [],
            action_items: [],
            discussion_points: [],
            // Initialize empty objects for more complex data
            company_profile: null,
            metrics: {},
            // Set a placeholder primary focus if not already defined
            primary_focus:
              customer.primary_focus ||
              (customer.prioritybucket
                ? `Priority: ${customer.prioritybucket}`
                : null),
          };
        })
        .filter(Boolean); // Filter out null values (customers without coordinates)

      setCustomers(customersWithIds);
      setFilteredCustomers(customersWithIds);

      // Clean up selectedCustomers state to remove references to customers that no longer exist
      const validCustomerIds = new Set(
        customersWithIds.map((c) => c._uniqueId),
      );
      setSelectedCustomers((prev) => {
        const cleaned = {};
        Object.keys(prev).forEach((id) => {
          if (validCustomerIds.has(id)) {
            cleaned[id] = prev[id];
          }
        });
        return cleaned;
      });

      console.log(
        "Fetched customers with valid coordinates:",
        customersWithIds.length,
      );
    } catch (err) {
      console.error("Error fetching customers:", err);

      // Provide more specific error messages based on error type
      let errorMessage = "Failed to fetch customers";

      if (
        err.code === "ECONNREFUSED" ||
        err.message.includes("Network Error")
      ) {
        errorMessage =
          "Cannot connect to backend server. Please check your connection and try again";
      } else if (err.response) {
        // Server responded with error status
        errorMessage = `Server error (${err.response.status}): ${err.response.statusText}`;
        console.error("Response data:", err.response.data);
        console.error("Response headers:", err.response.headers);
      } else if (err.request) {
        // Request was made but no response received
        errorMessage = "No response from server. Backend may be down.";
        console.error("Request details:", err.request);
      } else {
        // Something else happened
        errorMessage = err.message || "Unknown error occurred";
      }

      setCustomersError(errorMessage);
    } finally {
      setCustomersLoading(false);
    }
  };

  // Handle filter change
  const handleFilterChange = (filterType, value) => {
    setSelectedFilters((prev) => {
      const newFilters = { ...prev };

      if (newFilters[filterType].includes(value)) {
        // Remove the value if it's already selected
        newFilters[filterType] = newFilters[filterType].filter(
          (item) => item !== value,
        );
      } else {
        // Add the value if it's not already selected
        newFilters[filterType] = [...newFilters[filterType], value];
      }

      return newFilters;
    });
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedFilters({
      regions: [],
      states: [],
      markets: [],
      priority_buckets: [],
      priority_levels: [],
    });
    setSearchQuery("");
  };

  // Handle customer selection (memoized to prevent excessive re-renders)
  const handleCustomerSelect = useCallback(
    (uniqueId) => {
      setSelectedCustomers((prev) => {
        const newState = { ...prev };
        newState[uniqueId] = !prev[uniqueId];
        return newState;
      });

      // Also update selectedItems
      setSelectedItems((prev) => {
        const customer = customers.find((c) => c._uniqueId === uniqueId);
        if (!customer) return prev;

        const isCurrentlySelected = prev.some((item) => item.id === uniqueId);

        if (isCurrentlySelected) {
          // Remove customer and any of its opportunities
          return prev.filter(
            (item) =>
              item.id !== uniqueId && !item.id.startsWith(`${uniqueId}_opp_`),
          );
        } else {
          // Add customer as an item
          return [
            ...prev,
            {
              id: uniqueId,
              type: "customer",
              customer: customer,
              customername: customer.customername,
              formatted_address: customer.formatted_address,
              city: customer.city,
              state: customer.state,
              zipcode: customer.zipcode,
              coordinates: customer.coordinates,
              location: customer.location,
              prioritybucket: customer.prioritybucket,
              customercode: customer.customercode,
              ...customer,
              _uniqueId: uniqueId,
            },
          ];
        }
      });
    },
    [customers],
  );

  // Get priority badge variant
  const getPriorityBadgeVariant = (priority) => {
    switch (priority?.toLowerCase()) {
      case "high":
        return "destructive";
      case "medium":
        return "secondary";
      case "low":
        return "default";
      default:
        return "outline";
    }
  };

  // Get selected customers directions request for map (memoized with stability)
  const selectedCustomersDirectionsRequest = useMemo(() => {
    const waypoints = selectedItems
      .filter((item) => item.location)
      .map((item) => ({ location: item.location }));

    if (waypoints.length < 1) return null;

    // Create a stable object to prevent unnecessary re-renders
    const request = {
      origin: waypoints[0],
      destination: waypoints[waypoints.length - 1],
      waypoints: waypoints.slice(1, -1),
      optimizeWaypoints: waypoints.length > 2,
      travelMode: "DRIVING",
      // Add a key for change detection
      _key: JSON.stringify(waypoints.map((w) => w.location)),
    };

    return request;
  }, [selectedItems]);

  // Add handler for Google Maps
  const handleOpenGoogleMaps = () => {
    // Implement Google Maps handler
  };

  // Function to reset calendar events to their original positions
  const resetCalendarEvents = useCallback(() => {
    if (selectedItems.length === 0) {
      return;
    }

    // Get the current week's start date (Monday)
    const today = new Date();
    const currentWeekStart = startOfWeek(today, { weekStartsOn: 1 }); // 1 = Monday

    // Recreate events with original positioning logic
    const resetEvents = selectedItems
      .map((item, index) => {
        // Distribute events across the week - use modulo to repeat days if more than 5 items
        const dayOffset = index % 5; // Monday through Friday (0-4)

        // Create event date (current week start + day offset)
        const eventDate = new Date(currentWeekStart);
        eventDate.setDate(eventDate.getDate() + dayOffset);

        // Count how many items are assigned to this day already
        const itemsOnThisDay = selectedItems
          .slice(0, index)
          .filter((_, i) => i % 5 === dayOffset).length;

        const hour = 9 + itemsOnThisDay; // Start at 9 AM, then 10 AM, 11 AM, etc.

        // Set start and end times
        const startTime = new Date(eventDate);
        startTime.setHours(hour, 0, 0, 0);

        const endTime = new Date(startTime);
        endTime.setHours(startTime.getHours() + 1);

        return {
          id: item.id,
          title: item.customername || "Unnamed Customer",
          start: startTime,
          end: endTime,
          resource: item,
        };
      })
      .filter(Boolean);

    // Reset saved state when calendar is reset
    setItinerarySaved(false);

    // Update the calendar events
    setCalendarEvents(resetEvents);

    // Navigate to the first event date if available
    if (resetEvents.length > 0) {
      setCurrentDate(resetEvents[0].start);
    }

    toast({
      title: "Calendar reset",
      description: "Events have been reset to their original positions",
    });
  }, [selectedItems]);

  // Calendar navigation handler
  const handleCalendarNavigate = (date, view, action) => {
    let targetDate = date;

    // Validate the date
    if (!isValidDate(targetDate)) {
      console.warn(
        `Received invalid date: ${targetDate}. Falling back to current date.`,
      );
      targetDate = new Date();
    }

    if (action === "TODAY") {
      // If TODAY is clicked, reset events to original positions
      resetCalendarEvents();
      return;
    }

    setCurrentDate(targetDate);
  };

  // Custom toolbar for calendar
  const CustomToolbar = ({
    label,
    onView,
    onNavigate,
    views,
    view,
    isDragging,
  }) => {
    const goToBack = () => {
      console.log("CustomToolbar: Back button clicked");
      onNavigate("PREV");
    };
    const goToNext = () => {
      console.log("CustomToolbar: Next button clicked");
      onNavigate("NEXT");
    };
    const goToCurrent = () => {
      console.log("CustomToolbar: Reset button clicked");
      onNavigate("TODAY");
    };

    // Filter views based on available views
    const availableViews = views || ["week", "day"];

    return (
      <div className="grid grid-cols-1 sm:grid-cols-3 items-center px-2 py-3 bg-background border-b gap-2 sm:gap-0">
        {/* Navigation Buttons */}
        <div className="flex gap-1 justify-center sm:justify-start">
          <Button
            variant="outline"
            size="sm"
            onClick={goToBack}
            className="flex items-center gap-1 px-2"
            title="Previous week"
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Back</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={goToCurrent}
            className="px-2"
            title="Reset to original schedule"
          >
            Reset
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={goToNext}
            className="flex items-center gap-1 px-2"
            title="Next week"
          >
            <span className="hidden sm:inline">Next</span>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        {/* Label */}
        <div className="text-center">
          <span className="font-semibold text-base md:text-lg flex-1 whitespace-nowrap overflow-hidden text-ellipsis">
            {label}
          </span>
        </div>
        {/* View Buttons */}
        <div className="flex gap-1 justify-center sm:justify-end">
          {availableViews.map((v) => (
            <Button
              key={v}
              type="button"
              variant={v === view ? "default" : "outline"}
              size="sm"
              className="capitalize px-2"
              onClick={() => onView(v)}
            >
              {v}
            </Button>
          ))}
        </div>
      </div>
    );
  };

  // Event styling for calendar
  const eventStyleGetter = (event, start, end, isSelected) => {
    let backgroundColor = "hsl(var(--primary) / 0.85)";
    let color = "hsl(var(--primary-foreground))";
    let borderColor = "hsl(var(--primary))";

    const style = {
      backgroundColor,
      color,
      borderRadius: "calc(var(--radius) - 2px)",
      border: `1px solid ${borderColor}`,
      opacity: isSelected ? 1 : 0.9,
      display: "flex",
      flexDirection: "column",
      padding: "3px 6px",
      fontSize: "0.7rem",
      lineHeight: "1.3",
      boxShadow: isSelected
        ? "0 0 0 1px hsl(var(--background)), 0 0 0 3px hsl(var(--primary))"
        : "none",
      transition: "opacity 0.2s ease-in-out, background-color 0.2s ease-in-out",
      overflow: "hidden",
      cursor: "grab",
    };

    return { style };
  };

  // Custom event component
  const CustomEvent = ({ event }) => {
    const handleEventClick = (e) => {
      // Prevent click if user is dragging or interacting with delete button
      if (e.defaultPrevented) return;

      // Get customer data from event resource
      const customer = event.resource;
      if (customer) {
        handleOpenCustomerDialog(customer);
      }
    };

    return (
      <div
        title={event.title}
        className="relative pl-1.5 h-full group cursor-pointer"
        onClick={handleEventClick}
      >
        <strong className="text-xs font-medium block truncate">
          {event.title}
        </strong>
        <span className="text-xs opacity-80 block">
          {format(event.start, "h:mm a")} - {format(event.end, "h:mm a")}
        </span>

        {/* Delete Button (Visible on Hover) */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-0 right-0 h-5 w-5 p-0 text-destructive opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-10 bg-background/80 hover:bg-destructive hover:text-destructive-foreground rounded-full"
          onClick={(e) => {
            e.stopPropagation(); // Prevent calendar event click/drag actions
            handleRemoveEvent(event.id);
          }}
          aria-label="Remove item"
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
    );
  };

  // Add custom styles to fix the calendar background issue
  const calendarStyles = {
    className: "calendar-fix",
    style: {
      height: "100%",
    },
    dayPropGetter: (date) => {
      // Ensure all days have consistent styling
      return {
        className: "rbc-day-slot", // Apply the same class to all days
        style: {
          backgroundColor: "var(--background)",
          position: "relative",
          zIndex: 0,
        },
      };
    },
  };

  // Handle removing event
  const handleRemoveEvent = (eventId) => {
    handleCustomerSelect(eventId); // Unselect the customer
  };

  // Handle event drop
  const handleEventDrop = useCallback(
    ({ event, start, end }) => {
      // Check if event and resource are valid
      if (!event?.id || !start || !end) {
        console.error("Invalid arguments for handleEventDrop:", {
          event,
          start,
          end,
        });
        return;
      }

      // Reset saved state when events are moved
      setItinerarySaved(false);

      // Update the event in the calendarEvents array
      setCalendarEvents((prevEvents) => {
        return prevEvents.map((e) => {
          if (e.id === event.id) {
            return { ...e, start: new Date(start), end: new Date(end) };
          }
          return e;
        });
      });

      toast({
        title: "Event updated",
        description: "Customer moved to new time slot",
      });
    },
    [], // Remove calendarEvents dependency to prevent re-creation
  );

  // Define which events can be resized
  const resizableAccessor = useCallback(() => {
    // Allow all events to be resized
    return true;
  }, []);

  // Handle event resize
  const handleEventResize = useCallback(
    ({ event, start, end }) => {
      // Check if event and times are valid
      if (!event?.id || !start || !end) {
        console.error("Invalid arguments for handleEventResize:", {
          event,
          start,
          end,
        });
        return;
      }

      // Reset saved state when events are resized
      setItinerarySaved(false);
      setHasUnsavedChanges(true);

      // Update the state using functional update to avoid dependency issues
      setCalendarEvents((prevEvents) => {
        return prevEvents.map((e) => {
          if (e.id === event.id) {
            return {
              ...e,
              start: new Date(start),
              end: new Date(end),
            };
          }
          return e;
        });
      });

      toast({
        title: "Event duration updated",
        description: "Customer appointment time adjusted",
      });
    },
    [], // Remove calendarEvents dependency
  );

  // Cleanup effect for drag operations
  useEffect(() => {
    return () => {
      // Cleanup any pending timeouts and listeners on unmount
      if (autoNavTimeout) {
        clearTimeout(autoNavTimeout);
      }
      if (window.dragMouseCleanup) {
        window.dragMouseCleanup();
        delete window.dragMouseCleanup;
      }
    };
  }, [autoNavTimeout]);

  // Enhanced drag start handler
  const handleDragStart = useCallback(
    (args) => {
      console.log("Enhanced drag started:", args);
      setIsDragging(true);
      setDraggedEvent(args.event);

      // Simplified mouse move listener for edge detection
      const handleMouseMove = (e) => {
        const calendarContainer = document.querySelector(".rbc-calendar");

        if (calendarContainer) {
          const rect = calendarContainer.getBoundingClientRect();
          const edgeThreshold = 50;
          const autoNavDelay = 1000;

          // Clear existing timeout
          if (autoNavTimeout) {
            clearTimeout(autoNavTimeout);
            setAutoNavTimeout(null);
          }

          // Check if mouse is within calendar area vertically
          if (e.clientY >= rect.top && e.clientY <= rect.bottom) {
            // Check for right edge (next week)
            if (e.clientX > rect.right - edgeThreshold) {
              const timeout = setTimeout(() => {
                console.log("Auto-navigating to next week");
                setIsAutoNavigating(true);
                setPendingDrop({
                  event: args.event,
                  direction: "next",
                });
                const nextWeek = startOfWeek(addDays(currentDate, 7));
                handleCalendarNavigate(nextWeek, "week", "NEXT");
              }, autoNavDelay);
              setAutoNavTimeout(timeout);
            }
            // Check for left edge (previous week)
            else if (e.clientX < rect.left + edgeThreshold) {
              const timeout = setTimeout(() => {
                console.log("Auto-navigating to previous week");
                setIsAutoNavigating(true);
                setPendingDrop({
                  event: args.event,
                  direction: "prev",
                });
                const prevWeek = startOfWeek(addDays(currentDate, -7));
                handleCalendarNavigate(prevWeek, "week", "PREV");
              }, autoNavDelay);
              setAutoNavTimeout(timeout);
            }
          }
        }
      };

      document.addEventListener("mousemove", handleMouseMove);

      // Store cleanup function
      window.dragMouseCleanup = () => {
        document.removeEventListener("mousemove", handleMouseMove);
        if (autoNavTimeout) {
          clearTimeout(autoNavTimeout);
          setAutoNavTimeout(null);
        }
      };
    },
    [currentDate, autoNavTimeout],
  );

  // Enhanced drag end handler
  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    setDraggedEvent(null);
    setPendingDrop(null);
    setIsAutoNavigating(false);

    // Cleanup mouse move listener and timeout
    if (window.dragMouseCleanup) {
      window.dragMouseCleanup();
      delete window.dragMouseCleanup;
    }

    // Clear any pending auto-navigation timeout
    if (autoNavTimeout) {
      clearTimeout(autoNavTimeout);
      setAutoNavTimeout(null);
    }
  }, [autoNavTimeout]);

  // Enhanced event drop handler
  const handleEnhancedEventDrop = useCallback(
    ({ event, start, end }) => {
      // Use the pending drop event if available (cross-week drag)
      const eventToMove = pendingDrop?.event || event;

      // Ensure drag end cleanup is called
      handleDragEnd();

      // Check if event and resource are valid
      if (!eventToMove?.id || !start || !end) {
        console.error("Invalid arguments for handleEnhancedEventDrop:", {
          event: eventToMove,
          start,
          end,
        });
        toast({
          title: "Update Error",
          description: "Could not move the event.",
          variant: "destructive",
        });
        return;
      }

      // Reset saved state when events are moved
      setItinerarySaved(false);
      setHasUnsavedChanges(true);

      // Update the event in the calendarEvents array
      setCalendarEvents((prevEvents) => {
        return prevEvents.map((e) => {
          if (e.id === eventToMove.id) {
            return { ...e, start: new Date(start), end: new Date(end) };
          }
          return e;
        });
      });

      toast({
        title: "Event updated",
        description: "Customer moved to new time slot",
      });
    },
    [pendingDrop, handleDragEnd],
  );

  // Effect to handle pending drop after auto-navigation
  useEffect(() => {
    if (pendingDrop && isAutoNavigating) {
      const timer = setTimeout(() => {
        console.log("Restoring drag state after navigation", pendingDrop);

        // Restore drag visual feedback
        setIsDragging(true);
        setDraggedEvent(pendingDrop.event);

        setIsAutoNavigating(false);

        // Auto-trigger drop in the center of the new week after a short delay
        setTimeout(() => {
          // Calculate center of first day of the week
          const weekStart = startOfWeek(currentDate);
          const dropDate = weekStart;

          // Use 9 AM as default drop time
          const dropTime = new Date(dropDate);
          dropTime.setHours(9, 0, 0, 0);

          // Calculate end time (preserve original duration or use 1 hour default)
          const originalDuration = pendingDrop.event.end
            ? pendingDrop.event.end.getTime() -
              pendingDrop.event.start.getTime()
            : 60 * 60 * 1000; // 1 hour default
          const dropEndTime = new Date(dropTime.getTime() + originalDuration);

          console.log(`Auto-dropping at ${dropTime}`);

          // Trigger the drop handler
          handleEnhancedEventDrop({
            event: pendingDrop.event,
            start: dropTime,
            end: dropEndTime,
          });
        }, 500);
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [pendingDrop, isAutoNavigating, currentDate]);

  // Keyboard navigation effect for arrow keys
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Only handle arrow keys and prevent default browser navigation
      if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        // Don't interfere if user is typing in an input field (unless dragging)
        if (
          !isDragging &&
          (event.target.tagName === "INPUT" ||
            event.target.tagName === "TEXTAREA" ||
            event.target.isContentEditable)
        ) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        console.log(
          `[Keyboard Navigation] ${event.key} pressed, navigating calendar${isDragging ? " (while dragging)" : ""}`,
        );

        // During drag, we want the same behavior as the edge detection
        if (isDragging) {
          if (event.key === "ArrowLeft") {
            console.log("Keyboard navigation to previous week while dragging");
            setIsAutoNavigating(true);
            setPendingDrop({
              event: draggedEvent,
              direction: "prev",
            });
            const prevWeek = startOfWeek(addDays(currentDate, -7));
            handleCalendarNavigate(prevWeek, "week", "PREV");
          } else if (event.key === "ArrowRight") {
            console.log("Keyboard navigation to next week while dragging");
            setIsAutoNavigating(true);
            setPendingDrop({
              event: draggedEvent,
              direction: "next",
            });
            const nextWeek = startOfWeek(addDays(currentDate, 7));
            handleCalendarNavigate(nextWeek, "week", "NEXT");
          }
        } else {
          // Normal navigation when not dragging
          if (event.key === "ArrowLeft") {
            handleCalendarNavigate(currentDate, "week", "PREV");
          } else if (event.key === "ArrowRight") {
            handleCalendarNavigate(currentDate, "week", "NEXT");
          }
        }
      }
    };

    // Add event listener to the window for better event capture during drag
    window.addEventListener("keydown", handleKeyDown, true);

    // Cleanup function
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [currentDate, isDragging, draggedEvent]);

  // Handle saving the itinerary
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [itineraryName, setItineraryName] = useState("");
  const [itineraryDescription, setItineraryDescription] = useState("");

  const handleSaveItinerary = async () => {
    console.log("handleSaveItinerary called");
    
    // Extract customer IDs from calendar events instead of relying on selectedCustomers
    const customerIdsFromCalendar = [...new Set(calendarEvents.map(event => {
      if (event.resource && event.resource._uniqueId) {
        return event.resource._uniqueId;
      }
      // For simple customer IDs like "103156"
      if (event.id && !event.id.includes('_opp_')) {
        return event.id;
      }
      // For complex IDs like "cust_0_1750612847889_64qx0om", extract the original ID from resource
      if (event.resource && event.resource.customercode) {
        return event.resource.customercode;
      }
      return null;
    }).filter(Boolean))];
    
    console.log("Customer IDs from calendar:", customerIdsFromCalendar);
    console.log("Calendar events:", calendarEvents);

    if (customerIdsFromCalendar.length === 0) {
      toast({
        title: "No customers found",
        description:
          "Please add customers to the calendar to create an itinerary",
        variant: "destructive",
      });
      return;
    }

    if (calendarEvents.length === 0) {
      toast({
        title: "No schedule created",
        description:
          "Please arrange your customers on the calendar before saving",
        variant: "destructive",
      });
      return;
    }

    // Show dialog to get itinerary name
    setItineraryName(
      `Custom Itinerary - ${format(new Date(), "MMM dd, yyyy")}`,
    );
    setItineraryDescription(
      `Build your own itinerary with ${customerIdsFromCalendar.length} customers`,
    );
    console.log("Opening save dialog");
    setShowSaveDialog(true);
  };

  const loadItineraryForEdit = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `/api/itineraries/byoi/${editItineraryId}`,
      );

      if (response.data.success && response.data.data) {
        const itinerary = response.data.data;

        // Set itinerary name and description
        setItineraryName(itinerary.name);
        setItineraryDescription(itinerary.description);

        // Select customers that are in the itinerary
        const selectedMap = {};
        itinerary.schedule.forEach((stop) => {
          const customer = customers.find(
            (c) => c.customercode === stop.customer_id,
          );
          if (customer) {
            selectedMap[customer._uniqueId] = true;
          }
        });
        setSelectedCustomers(selectedMap);

        // Transform schedule back to calendar events
        const events = itinerary.schedule
          .map((stop, index) => {
            const customer = customers.find(
              (c) => c.customercode === stop.customer_id,
            );
            if (!customer) return null;

            return {
              id: `${stop.customer_id}-${index}`,
              title: stop.customer_name,
              start: new Date(stop.start_time),
              end: new Date(stop.end_time),
              resource: {
                ...customer,
                customercode: stop.customer_id,
                customername: stop.customer_name,
                formatted_address: stop.address,
                location: stop.coordinates,
                prioritybucket: stop.priority,
                ...stop.customer_details,
              },
            };
          })
          .filter(Boolean);

        setCalendarEvents(events);

        toast({
          title: "Itinerary loaded",
          description: "You can now edit your saved itinerary",
        });
      }
    } catch (error) {
      console.error("Error loading itinerary:", error);
      toast({
        title: "Failed to load itinerary",
        description: "Please try again later",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSave = async () => {
    console.log("handleConfirmSave called");
    console.log("Itinerary name:", itineraryName);
    console.log("Itinerary description:", itineraryDescription);
    
    if (!itineraryName.trim()) {
      toast({
        title: "Name required",
        description: "Please enter a name for your itinerary",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    setShowSaveDialog(false);

    try {
      // Prepare itinerary payload
      // Create schedule from calendar events
      const schedule = calendarEvents.map((event, index) => {
        const item = event.resource; // This is the selectedItem

        // Parse coordinates if they're in string format "lat,lng"
        let coordinates = null;
        if (item.coordinates) {
          if (typeof item.coordinates === 'string') {
            const [lat, lng] = item.coordinates.split(',').map(Number);
            coordinates = { lat, lng };
          } else if (item.coordinates.lat && item.coordinates.lng) {
            coordinates = item.coordinates;
          }
        } else if (item.lat && item.lng) {
          coordinates = { lat: Number(item.lat), lng: Number(item.lng) };
        }

        return {
          customer_id: item.customercode || item.customercode || item._uniqueId,
          customer_name: item.customername,
          opportunity_id: item.type === "opportunity" ? item.opportunity?.opportunitynum : undefined,
          opportunity_address: item.type === "opportunity" ? item.formatted_address : undefined,
          start_time: event.start.toISOString(),
          end_time: event.end.toISOString(),
          address: item.formatted_address || item.location,
          city: item.city || "",
          state: item.state || "",
          zipcode: item.zipcode || "",
          location: item.formatted_address || item.location,
          coordinates: coordinates,
          priority: (item.prioritybucket || "Medium").toLowerCase(),
          primary_focus: item.primary_focus || "visit",
          order: index + 1,
          status: "PENDING",
          notes: "",
          customer_details: {
            sales_region: item.salesregion,
            state: item.state,
            market: item.market,
            priority_bucket: item.prioritybucket,
            primary_focus: item.primary_focus,
            selected_opportunities: item.opportunity ? [item.opportunity] : [],
            engagement_data: {
              last_contact: item.last_visit_date || null,
              contact_frequency: item.visit_frequency || null
            }
          }
        };
      });

      const payload = {
        name: itineraryName.trim(),
        description:
          itineraryDescription.trim() ||
          `Build your own itinerary with ${schedule.length} stops`,
        user_id: user?.id || user?.username || "unknown",
        total_customers: calendarEvents.length,
        estimated_duration: Math.round(
          calendarEvents.reduce((total, event) => {
            return total + (event.end - event.start) / (1000 * 60);
          }, 0),
        ),
        metadata: {
          created_by: user?.email || user?.username || "Business Manager",
          creation_method: "manual",
          week_of: format(
            startOfWeek(new Date(), { weekStartsOn: 1 }),
            "yyyy-MM-dd'T'HH:mm:ss'Z'",
          ),
        },
        schedule: schedule,
      };

      console.log("Sending payload:", payload);
      console.log("Edit mode:", editItineraryId ? "UPDATE" : "CREATE");
      
      const response = editItineraryId
        ? await axios.put(`/api/itineraries/byoi/${editItineraryId}`, payload)
        : await axios.post("/api/itineraries/save", payload);

      console.log("API response:", response);
      
      if (response.data.success) {
        toast({
          title: editItineraryId
            ? "Itinerary updated successfully"
            : "Itinerary saved successfully",
          description: `${editItineraryId ? "Updated" : "Created"} "${payload.name}" with ${schedule.length} stops`,
          action: {
            label: "View Saved",
            onClick: () => navigate("/saved-byoi"),
          },
        });

        // Set saved state for visual feedback
        setItinerarySaved(true);

        // Reset after a delay to allow user to see the success state
        setTimeout(() => {
          setItinerarySaved(false);
          setSelectedCustomers({});
          setSelectedItems([]);
          setCalendarEvents([]);
        }, 3000);
      } else {
        // Handle case where API returns but success is false
        console.error("API returned success: false", response.data);
        throw new Error(response.data.message || "Failed to save itinerary");
      }
    } catch (error) {
      console.error("Error saving itinerary:", error);
      console.error("Error response:", error.response);
      console.error("Error details:", {
        status: error.response?.status,
        data: error.response?.data,
        headers: error.response?.headers
      });
      toast({
        title: "Failed to save itinerary",
        description:
          error.response?.data?.message ||
          error.message ||
          "Please try again later",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // --- ICS Export Logic ---
  const generateICSContent = () => {
    const eventsForICS = calendarEvents
      .map((event) => {
        // Skip if dates are invalid
        if (!isValidDate(event.start) || !isValidDate(event.end)) {
          return null; // Skip this event
        }

        return {
          title: event.title,
          start: [
            event.start.getFullYear(),
            event.start.getMonth() + 1, // Month is 0-indexed
            event.start.getDate(),
            event.start.getHours(),
            event.start.getMinutes(),
          ],
          end: [
            event.end.getFullYear(),
            event.end.getMonth() + 1,
            event.end.getDate(),
            event.end.getHours(),
            event.end.getMinutes(),
          ],
          description: `Visit ${event.title}`,
          location: event.resource?.location || "", // Add location if available
          uid: `customer-${event.id}-${Date.now()}`, // Basic unique ID
        };
      })
      .filter(Boolean); // Remove null entries from invalid dates

    if (eventsForICS.length === 0) {
      toast({
        title: "Export Warning",
        description: "No valid event data to export.",
        variant: "default",
      });
      return null; // Return null if no events
    }

    let result;
    try {
      result = ics.createEvents(eventsForICS);
    } catch (e) {
      console.error("Error calling ics.createEvents:", e);
      toast({
        title: "Export Error",
        description: `Failed during ICS generation: ${e.message}`,
        variant: "destructive",
      });
      return null; // Return null on catch
    }

    // Now destructure after logging and try-catch
    const { value: icsString, error } = result || {};

    if (error) {
      console.error("Error reported by ics.createEvents:", error);
      toast({
        title: "Export Error",
        description: "Could not generate ICS file content.",
        variant: "destructive",
      });
      return null;
    }

    if (!icsString) {
      console.error(
        "ics.createEvents succeeded but returned undefined/null content.",
      );
      toast({
        title: "Export Error",
        description: "Failed to generate ICS content string.",
        variant: "destructive",
      });
      return null;
    }

    return icsString;
  };

  const handleExportICS = () => {
    const icsContent = generateICSContent();

    if (!icsContent) {
      console.warn(
        "No ICS content generated or an error occurred. Aborting export.",
      );
      return;
    }

    try {
      const blob = new Blob([icsContent], {
        type: "text/calendar;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `isco_itinerary_export_${new Date().toISOString().split("T")[0]}.ics`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast({
        title: "Success",
        description: "Schedule exported as ICS file.",
      });
    } catch (downloadError) {
      console.error("Error during blob creation or download:", downloadError);
      toast({
        title: "Export Error",
        description: "Failed to initiate file download.",
        variant: "destructive",
      });
    }
  };
  // --- End ICS Export Logic ---

  // Calculate if there are any selected customers
  const hasSelectedCustomers = selectedItems.length > 0;
  const selectedCustomerCount =
    Object.values(selectedCustomers).filter(Boolean).length;

  // Calculate total stops including opportunities
  const totalStopsCount = selectedItems.length;

  // Memoized selected customers for map ordered by calendar events
  const selectedCustomersForMap = useMemo(() => {
    if (calendarEvents.length === 0) {
      // Fallback to original selection order if no calendar events
      const itemsWithCoords = selectedItems.filter((item) => item.location);

      // Log items without coordinates (for debugging)
      const itemsWithoutCoords = selectedItems.filter(
        (item) => !item.location && item.formatted_address,
      );
      if (itemsWithoutCoords.length > 0) {
        console.warn(
          `${itemsWithoutCoords.length} items have addresses but no coordinates:`,
          itemsWithoutCoords.map((item) => ({
            name: item.customername,
            address: item.formatted_address,
          })),
        );
      }

      return itemsWithCoords;
    }

    // Order customers based on calendar event order (chronological)
    const sortedEvents = [...calendarEvents].sort(
      (a, b) => new Date(a.start) - new Date(b.start),
    );

    const orderedCustomers = sortedEvents
      .map((event) => {
        const resource = event.resource || {};
        // Find matching customer from selectedItems or use the resource directly
        const matchingCustomer = selectedItems.find(
          (item) =>
            item._uniqueId === resource._uniqueId ||
            item.id === resource.id ||
            item.customername === resource.customername,
        );

        return (
          matchingCustomer || {
            _uniqueId: resource._uniqueId || resource.id || event.id,
            customername: resource.customername || resource.name || event.title,
            location:
              resource.location ||
              resource.coordinates ||
              `${resource.lat},${resource.lng}`,
            lat: resource.lat,
            lng: resource.lng,
            formatted_address: resource.formatted_address || resource.address,
            ...resource,
          }
        );
      })
      .filter((customer) => customer.location); // Only include items with valid location data

    return orderedCustomers;
  }, [calendarEvents, selectedItems]);

  // Apply custom styles to fix calendar grid issues
  useEffect(() => {
    // Add custom styles to the document
    const styleEl = document.createElement("style");
    styleEl.textContent = calendarFixStyles;
    document.head.appendChild(styleEl);

    // Clean up on component unmount
    return () => {
      document.head.removeChild(styleEl);
    };
  }, []);

  // Add effect to fetch churn data when customer data is loaded
  useEffect(() => {
    const fetchChurnDataForCustomers = async () => {
      if (customers && customers.length > 0) {
        // Extract customer codes from the customer data
        const customerCodes = customers
          .map((customer) => {
            // Try to get code from different possible field names
            let code =
              customer.customercode ||
              customer.CustomerCode ||
              customer.customer_code ||
              customer.id;

            // Ensure the code is a string
            if (code !== undefined && code !== null) {
              code = String(code);
            }
            return code;
          })
          .filter((code) => code); // Filter out undefined or null

        if (customerCodes.length > 0) {
          await fetchChurnData(customerCodes);
        }
      }
    };

    fetchChurnDataForCustomers();
  }, [customers, fetchChurnData]);

  // Open customer dialog function
  const handleOpenCustomerDialog = (customer) => {
    setSelectedCustomerForDialog(customer);
    if (customer && customer.customercode) {
      const customerCode = customer.customercode;
      fetchCustomerDetailsForDialog(customerCode);
      fetchCustomerOpportunitiesForEnhancedDialog(customerCode);
    }
  };

  // Handle customer click for opportunity selection
  const handleCustomerClickForOpportunities = (customer) => {
    setSelectedCustomerForOpportunities(customer);
    setShowOpportunities(true);
    setSelectedOpportunityIds([]);
    fetchCustomerOpportunities(customer.customercode);
  };

  // Fetch opportunities for a specific customer
  const fetchCustomerOpportunities = async (customerCode) => {
    if (!customerCode) return;

    setOpportunitiesLoading(true);
    setOpportunitiesError(null);

    try {
      const response = await axios.get(
        `/customers/${customerCode}/opportunities`,
      );

      if (response.data && Array.isArray(response.data.data)) {
        setCustomerOpportunities(response.data.data);
      } else if (response.data && Array.isArray(response.data.opportunities)) {
        setCustomerOpportunities(response.data.opportunities);
      } else if (response.data && Array.isArray(response.data)) {
        setCustomerOpportunities(response.data);
      } else {
        setCustomerOpportunities([]);
      }
    } catch (err) {
      console.error(
        `Error fetching opportunities for customer ${customerCode}:`,
        err,
      );
      setOpportunitiesError(
        err.response?.data?.message ||
          err.message ||
          "Failed to load customer opportunities",
      );
      setCustomerOpportunities([]);
    } finally {
      setOpportunitiesLoading(false);
    }
  };

  // Handle opportunity selection
  const handleOpportunitySelection = (opportunityId, isSelected) => {
    setSelectedOpportunityIds((prev) =>
      isSelected
        ? [...prev, opportunityId]
        : prev.filter((id) => id !== opportunityId),
    );
  };

  // Handle adding selected opportunities to itinerary
  const handleAddOpportunitiesToItinerary = () => {
    if (selectedOpportunityIds.length === 0) {
      toast({
        title: "No opportunities selected",
        description:
          "Please select at least one opportunity to add to your itinerary",
        variant: "destructive",
      });
      return;
    }

    // Get selected opportunities data
    const selectedOpportunitiesData = customerOpportunities.filter(
      (opp, index) => {
        const oppId = `${opp.type || "unknown"}-${opp.projectcity || "unknown"}-${opp.projectstate || "unknown"}-${index}`;
        return selectedOpportunityIds.includes(oppId);
      },
    );

    // Add each opportunity as a separate item
    const newItems = selectedOpportunitiesData.map((opp, index) => {
      // Determine if opportunity has valid location data
      const hasCoordinates =
        opp.coordinates && opp.coordinates.lat && opp.coordinates.lng;
      const oppCity = opp.city || opp.projectcity;
      const oppState = opp.state || opp.projectstate;

      return {
        // First spread customer data
        ...selectedCustomerForOpportunities,
        // Then override with opportunity-specific data
        id: `${selectedCustomerForOpportunities._uniqueId}_opp_${opp.opportunitynum || index}`,
        type: "opportunity",
        customer: selectedCustomerForOpportunities,
        opportunity: opp,
        customername: `${selectedCustomerForOpportunities.customername} - ${opp.type || "Opportunity"} ${opp.opportunitynum}`,
        formatted_address:
          opp.address ||
          (oppCity && oppState ? `${oppCity}, ${oppState}` : null),
        city: oppCity,
        state: oppState,
        zipcode: opp.zipcode,
        coordinates: hasCoordinates ? opp.coordinates : null,
        lat: hasCoordinates ? opp.coordinates.lat : null,
        lng: hasCoordinates ? opp.coordinates.lng : null,
        // CRITICAL: Set location to null if no coordinates, so MapSection won't use parent's location
        location: hasCoordinates
          ? `${opp.coordinates.lat},${opp.coordinates.lng}`
          : null, // Don't fall back to parent location!
        prioritybucket: selectedCustomerForOpportunities.prioritybucket,
        customercode: selectedCustomerForOpportunities.customercode,
        _uniqueId: `${selectedCustomerForOpportunities._uniqueId}_opp_${opp.opportunitynum || index}`,
      };
    });

    // Add to selected items
    setSelectedItems((prev) => {
      // Remove any existing opportunities for this customer to avoid duplicates
      // But keep the customer itself if it was selected
      const filtered = prev.filter(
        (item) =>
          !item.id.startsWith(
            `${selectedCustomerForOpportunities._uniqueId}_opp_`,
          ),
      );
      return [...filtered, ...newItems];
    });

    // Show success message
    toast({
      title: "Added to itinerary",
      description: `Added ${selectedOpportunityIds.length} opportunities from ${selectedCustomerForOpportunities.customername} to your itinerary`,
    });

    // Return to customer list
    setShowOpportunities(false);
    setSelectedCustomerForOpportunities(null);
    setSelectedOpportunityIds([]);
  };

  // Handle back to customer list
  const handleBackToCustomerList = () => {
    setShowOpportunities(false);
    setSelectedCustomerForOpportunities(null);
    setSelectedOpportunityIds([]);
  };

  // Fetch customer details for enhanced dialog (matching ItineraryDetailsPage)
  const fetchCustomerDetailsForDialog = useCallback(
    async (customerCode) => {
      if (
        !customerCode ||
        customerDetailsData[customerCode] ||
        customerDetailsLoading[customerCode]
      ) {
        return; // Already have data or loading
      }

      setCustomerDetailsLoading((prev) => ({ ...prev, [customerCode]: true }));

      try {
        // Assuming getCustomerDetails is imported from a service
        const response = await getCustomerDetails(customerCode, token);
        setCustomerDetailsData((prev) => ({
          ...prev,
          [customerCode]: response,
        }));
      } catch (error) {
        console.error("Error fetching customer details:", error);
        toast({
          title: "Failed to load customer details",
          description: "Please try again later",
          variant: "destructive",
        });
      } finally {
        setCustomerDetailsLoading((prev) => ({
          ...prev,
          [customerCode]: false,
        }));
      }
    },
    [customerDetailsData, customerDetailsLoading, token],
  );

  // Fetch customer opportunities for enhanced dialog
  const fetchCustomerOpportunitiesForEnhancedDialog = useCallback(
    async (customerCode) => {
      if (
        !customerCode ||
        customerOpportunitiesData[customerCode] ||
        customerOpportunitiesLoading[customerCode]
      ) {
        return; // Already have data or loading
      }

      setCustomerOpportunitiesLoading((prev) => ({
        ...prev,
        [customerCode]: true,
      }));

      try {
        // Assuming getCustomerOpportunities is imported from a service
        const response = await getCustomerOpportunities(customerCode, token);
        setCustomerOpportunitiesData((prev) => ({
          ...prev,
          [customerCode]: response,
        }));
      } catch (error) {
        console.error("Error fetching customer opportunities:", error);
        // Don't show toast for opportunities - they're not critical for display
      } finally {
        setCustomerOpportunitiesLoading((prev) => ({
          ...prev,
          [customerCode]: false,
        }));
      }
    },
    [customerOpportunitiesData, customerOpportunitiesLoading, token],
  );

  // Custom dialog content for customer details - comprehensive implementation
  const CustomerDialogContent = ({
    customer,
    isLoading,
    customerDetailsData,
    customerOpportunitiesData,
    setSelectedCustomerOpportunities,
    setOpportunitiesDialogOpen,
  }) => {
    const { loading: churnLoading, getChurnDataForCustomer } = useChurnData();

    if (!customer) return null;

    // Get customer details from enhanced data or fallback to basic customer data
    const customerCode = customer.customercode || customer.id;
    const customerDetails = customerDetailsData[customerCode] || customer;
    const customerOpportunities = customerOpportunitiesData[customerCode];

    // Show loading state when fetching additional details
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-center space-y-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">Loading customer details...</p>
              <p className="text-xs text-muted-foreground">Fetching business metrics, opportunities, and insights</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <ScrollArea className="max-h-[70vh]">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 p-3 sm:p-4 md:p-6">
          {/* Left Column */}
          <div className="space-y-3 sm:space-y-4">
            {/* Combined Customer & Company Info Card */}
            <Card className="bg-background shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                  <Building2 className="w-4 h-4" />
                  Customer & Company Info
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Customer Basic Info */}
                  <div className="space-y-2">
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Customer Details</h4>
                    <div className="space-y-1.5 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Name:</span>
                        <span className="font-medium">{customer.customername || "Unknown Customer"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">ID:</span>
                        <span className="font-medium">{customer.customercode || customer.id || "N/A"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Address:</span>
                        <span className="font-medium text-right flex-1 ml-2">{customerDetails.formatted_address || customer.formatted_address || "No address provided"}</span>
                      </div>
                      {(customerDetails.city || customerDetails.state || customerDetails.zip_code) && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Location:</span>
                          <span className="font-medium">
                            {[customerDetails.city, customerDetails.state, customerDetails.zip_code].filter(Boolean).join(", ")}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Customer Notes */}
                  {customerDetails.notes && (
                    <div className="space-y-2 pt-2 border-t">
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Notes</h4>
                      <p className="text-sm text-foreground bg-muted/30 rounded p-2">{customerDetails.notes}</p>
                    </div>
                  )}

                  {/* Company Profile Info */}
                  {customerDetails?.company_profile && (
                    <div className="space-y-2 pt-2 border-t">
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Company Profile</h4>
                      <div className="space-y-1.5 text-sm">
                        {customerDetails.company_profile.industry && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Industry:</span>
                            <Badge variant="secondary" className="text-xs">{customerDetails.company_profile.industry}</Badge>
                          </div>
                        )}
                        {customerDetails.company_profile.website_url && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Website:</span>
                            <a href={customerDetails.company_profile.website_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1">
                              <Globe className="w-3 h-3" />
                              Visit
                            </a>
                          </div>
                        )}
                        {customerDetails.company_profile.company_summary && (
                          <div className="space-y-1">
                            <span className="text-muted-foreground text-xs">Summary:</span>
                            <p className="text-xs text-foreground leading-relaxed bg-muted/30 rounded p-2">{customerDetails.company_profile.company_summary}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Discussion Points Card */}
            {customerDetails?.discussion_points && customerDetails.discussion_points.length > 0 && (
              <Card className="bg-background shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                    <MessagesSquare className="w-4 h-4" />
                    Discussion Points
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {customerDetails.discussion_points.map((point, i) => (
                      <li key={i} className="flex gap-2 items-start">
                        <MessageSquare className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                        <span className="text-sm">{point}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* QORC Analysis Card */}
            {customerDetails?.qorc_scores && (
              <Card className="bg-gradient-to-br from-slate-50 to-gray-50 dark:from-slate-900/50 dark:to-gray-900/50 shadow-md border-slate-200 dark:border-slate-700">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <div className="p-1.5 bg-primary/10 rounded-lg">
                      <BarChart3 className="w-4 h-4 text-primary" />
                    </div>
                    Customer Scorecard
                  </CardTitle>
                  {customerDetails.qorc_scores.prioritybucket && (
                    <Badge 
                      variant={
                        customerDetails.qorc_scores.prioritybucket === 'High' ? 'default' : 
                        customerDetails.qorc_scores.prioritybucket === 'Medium' ? 'secondary' : 'outline'
                      }
                      className="text-xs font-medium px-3 py-1"
                    >
                      {customerDetails.qorc_scores.prioritybucket} Priority
                    </Badge>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-muted/30 border rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-foreground">{customerDetails.qorc_scores.qscore || 'N/A'}</p>
                      <p className="text-xs text-muted-foreground font-medium">Q-Score</p>
                    </div>
                    <div className="bg-muted/30 border rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-foreground">{customerDetails.qorc_scores.oscore || 'N/A'}</p>
                      <p className="text-xs text-muted-foreground font-medium">O-Score</p>
                    </div>
                    <div className="bg-muted/30 border rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-foreground">{customerDetails.qorc_scores.rscore || 'N/A'}</p>
                      <p className="text-xs text-muted-foreground font-medium">R-Score</p>
                    </div>
                    <div className="bg-muted/30 border rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-foreground">{customerDetails.qorc_scores.cscore || 'N/A'}</p>
                      <p className="text-xs text-muted-foreground font-medium">C-Score</p>
                    </div>
                  </div>
                  <div className="bg-muted/20 rounded-lg p-3 border">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-muted-foreground font-medium">Priority Score:</span>
                      <span className="text-lg font-bold text-foreground">{customerDetails.qorc_scores.priorityscore || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground font-medium">Ranking:</span>
                      <Badge variant="outline" className="text-xs font-semibold">
                        #{customerDetails.qorc_scores.priorityranking || 'N/A'}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column */}
          <div className="space-y-3 sm:space-y-4">
            {/* Business Metrics Card */}
            {customerDetails?.business_metrics && (
              <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 shadow-md border-green-200 dark:border-green-700">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <div className="p-1.5 bg-green-100 dark:bg-green-900 rounded-lg">
                      <DollarSign className="w-4 h-4 text-green-600 dark:text-green-400" />
                    </div>
                    Business Performance
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-muted/30 border rounded-lg p-3">
                      <p className="text-lg font-bold text-foreground">
                        {customerDetails.business_metrics.order_count || 0}
                      </p>
                      <p className="text-xs text-muted-foreground font-medium">Total Orders</p>
                      <p className="text-sm font-semibold text-foreground mt-1">
                        ${(customerDetails.business_metrics.order_value || 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="bg-muted/30 border rounded-lg p-3">
                      <p className="text-lg font-bold text-foreground">
                        {customerDetails.business_metrics.quote_count || 0}
                      </p>
                      <p className="text-xs text-muted-foreground font-medium">Total Quotes</p>
                      <p className="text-sm font-semibold text-foreground mt-1">
                        ${(customerDetails.business_metrics.quote_value || 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="bg-muted/30 border rounded-lg p-3 text-center">
                      <p className="text-lg font-bold text-foreground">
                        {customerDetails.business_metrics.rental_count || 0}
                      </p>
                      <p className="text-xs text-muted-foreground font-medium">Rentals</p>
                    </div>
                    <div className="bg-muted/30 border rounded-lg p-3 text-center">
                      <p className="text-lg font-bold text-foreground">
                        {customerDetails.business_metrics.churn_rank || 'N/A'}
                      </p>
                      <p className="text-xs text-muted-foreground font-medium">Churn Risk</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Active Opportunities Card */}
            {(() => {
              // Calculate counts from opportunities response
              let quoteCount = 0;
              let orderCount = 0;
              let rentalCount = 0;
              
              if (customerOpportunities?.data) {
                customerOpportunities.data.forEach(opp => {
                  if (opp.type === 'quote') quoteCount++;
                  else if (opp.type === 'order') orderCount++;
                  else if (opp.type === 'rental') rentalCount++;
                });
              }
              
              return (
                <Card className="bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-900/30 dark:to-blue-900/30 shadow-md border-indigo-200 dark:border-indigo-700">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <div className="p-1.5 bg-indigo-100 dark:bg-indigo-900 rounded-lg">
                        <Target className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                      </div>
                      Active Quotes, Orders and Rentals
                    </CardTitle>
                    {/* View All Button */}
                    {(quoteCount > 0 || orderCount > 0 || rentalCount > 0) && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-xs h-6"
                        onClick={() => {
                          // Format opportunities for the dialog  
                          const allOpportunities = customerOpportunities?.data || [];
                          const quotes = allOpportunities.filter(opp => opp.type === 'quote');
                          const orders = allOpportunities.filter(opp => opp.type === 'order');
                          const rentals = allOpportunities.filter(opp => opp.type === 'rental');
                          
                          setSelectedCustomerOpportunities({
                            customer: customer,
                            details: {
                              ...customerDetails,
                              open_quotes: quotes,
                              open_orders: orders,
                              active_rentals: rentals
                            },
                            opportunities: customerOpportunities?.data || []
                          });
                          setOpportunitiesDialogOpen(true);
                        }}
                      >
                        View All
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent>
                    {/* Summary counts */}
                    <div className="grid grid-cols-3 gap-3 text-center mb-4">
                      <div className="bg-muted/30 border rounded-lg p-3">
                        <p className="text-xl font-bold text-foreground">{quoteCount}</p>
                        <p className="text-xs text-muted-foreground font-medium">Quotes</p>
                      </div>
                      <div className="bg-muted/30 border rounded-lg p-3">
                        <p className="text-xl font-bold text-foreground">{orderCount}</p>
                        <p className="text-xs text-muted-foreground font-medium">Orders</p>
                      </div>
                      <div className="bg-muted/30 border rounded-lg p-3">
                        <p className="text-xl font-bold text-foreground">{rentalCount}</p>
                        <p className="text-xs text-muted-foreground font-medium">Rentals</p>
                      </div>
                    </div>

                    {/* Details - only show if there are opportunities */}
                    {(quoteCount > 0 || orderCount > 0 || rentalCount > 0) && customerOpportunities?.data && (
                      <div className="space-y-3 max-h-48 overflow-y-auto">
                        {/* Show opportunities grouped by type */}
                        {customerOpportunities.data
                          .filter(opp => opp.type === 'quote')
                          .slice(0, 3)
                          .map((quote, idx) => (
                          <div key={idx} className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800/30 rounded p-2">
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-1">
                                <FileText className="w-3 h-3 text-blue-600" />
                                <span className="text-xs font-semibold">Quote</span>
                              </div>
                              <span className="text-xs font-bold text-blue-600">
                                ${(quote.estimatedvalue || 0).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-xs font-medium truncate">{quote.opportunityname}</p>
                            <p className="text-xs text-muted-foreground">
                              #{quote.opportunitynum} {quote.markettype ? `• ${quote.markettype}` : ''}
                            </p>
                          </div>
                        ))}
                        {quoteCount > 3 && (
                          <p className="text-xs text-muted-foreground text-center">
                            +{quoteCount - 3} more quotes
                          </p>
                        )}
                        
                        {/* Orders */}
                        {customerOpportunities.data
                          .filter(opp => opp.type === 'order')
                          .slice(0, 2)
                          .map((order, idx) => (
                          <div key={idx} className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800/30 rounded p-2">
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3 text-green-600" />
                                <span className="text-xs font-semibold">Order</span>
                              </div>
                              <span className="text-xs font-bold text-green-600">
                                ${(order.estimatedvalue || 0).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-xs font-medium truncate">{order.opportunityname}</p>
                            <p className="text-xs text-muted-foreground">
                              #{order.opportunitynum} {order.markettype ? `• ${order.markettype}` : ''}
                            </p>
                          </div>
                        ))}
                        
                        {/* Rentals */}
                        {customerOpportunities.data
                          .filter(opp => opp.type === 'rental')
                          .slice(0, 2)
                          .map((rental, idx) => (
                          <div key={idx} className="bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-800/30 rounded p-2">
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-1">
                                <Package className="w-3 h-3 text-purple-600" />
                                <span className="text-xs font-semibold">Rental</span>
                              </div>
                              <span className="text-xs font-bold text-purple-600">
                                ${(rental.estimatedvalue || 0).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-xs font-medium truncate">{rental.opportunityname}</p>
                            <p className="text-xs text-muted-foreground">
                              #{rental.opportunitynum}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })()}

            {/* Action Items Card */}
            {customerDetails?.action_items && customerDetails.action_items.length > 0 && (
              <Card className="bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/30 dark:to-yellow-900/30 shadow-md border-amber-200 dark:border-amber-700">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <div className="p-1.5 bg-amber-100 dark:bg-amber-900 rounded-lg">
                      <ListChecks className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                    </div>
                    Action Items ({customerDetails.action_items.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {customerDetails.action_items.map((action, i) => (
                      <div key={i} className="group flex items-start gap-3 bg-muted/20 hover:bg-muted/30 border rounded-lg p-3 transition-colors">
                        <CheckSquare className="w-4 h-4 mt-0.5 text-muted-foreground group-hover:text-foreground flex-shrink-0 transition-colors" />
                        <div className="flex-1">
                          <p className="text-sm text-foreground leading-relaxed">{action}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant="outline" className="text-xs">
                              Item #{i + 1}
                            </Badge>
                            <span className="text-xs text-muted-foreground">Priority: Normal</span>
                          </div>
                        </div>
                      </div>
                    ))}
                    {customerDetails.action_items.length === 0 && (
                      <div className="text-center py-4 text-muted-foreground">
                        <ClipboardCheck className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No action items found</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </ScrollArea>
    );
  };

  // If there's a loading error, display it
  if (customersError && !customersLoading) {
    return (
      <div>
        <Header />
        <div className="p-8 text-center">
          <div className="mb-4 text-xl font-semibold">
            Error Loading Customers
          </div>
          <div className="text-red-500 mb-4">{customersError}</div>
          <Button onClick={() => window.location.reload()} variant="outline">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // Handler for Schedule Fill Assist
  const handleScheduleFillAssist = async () => {
    if (calendarEvents.length === 0 || fillAssistLoading) return;
    setFillAssistLoading(true);
    try {
      // Gather coordinates from selected stops (calendarEvents)
      const stops = calendarEvents
        .map((event) => {
          const resource = event.resource || {};
          let lat = resource.lat || (resource.coordinates && resource.coordinates.lat);
          let lng = resource.lng || (resource.coordinates && resource.coordinates.lng);
          
          // For opportunities, also check if coordinates are nested in opportunity object
          if (!lat && !lng && resource.opportunity && resource.opportunity.coordinates) {
            lat = resource.opportunity.coordinates.lat;
            lng = resource.opportunity.coordinates.lng;
          }
          
          return {
            id: resource._uniqueId || resource.id || event.id,
            name: resource.customername || resource.title || event.title,
            lat,
            lng,
          };
        })
        .filter((stop) => stop.lat && stop.lng);

      // Send to backend
      const response = await axios.post("/itinerary/fill-assist", {
        stops,
        num_days: numDays,
      });

      if (response.data && response.data.stops) {
        // Create new events from response
        const newEvents = response.data.stops.map((stop, idx) => {
          return {
            id: stop.id || stop.customer_code || `stop_${idx}`,
            title: stop.name || `Stop ${idx + 1}`,
            start: new Date(stop.start),
            end: new Date(stop.end),
            resource: {
              _uniqueId: stop.id || stop.customer_code || `stop_${idx}`,
              id: stop.customer_code || stop.id,
              customername: stop.name || `Stop ${idx + 1}`,
              location: `${stop.lat},${stop.lng}`,
              lat: stop.lat,
              lng: stop.lng,
              formatted_address: stop.address || `${stop.lat}, ${stop.lng}`,
              customer_code: stop.customer_code,
              opportunity_id: stop.opportunity_id,
              is_suggested: stop.is_suggested,
              ...stop,
            },
          };
        });

        // Create updated selected items
        const updatedSelectedItems = response.data.stops.map((stop, idx) => ({
          _uniqueId: stop.id || stop.customer_code || `stop_${idx}`,
          id: stop.customer_code || stop.id,
          customername: stop.name || `Stop ${idx + 1}`,
          location: `${stop.lat},${stop.lng}`,
          lat: stop.lat,
          lng: stop.lng,
          formatted_address: stop.address || `${stop.lat}, ${stop.lng}`,
          customer_code: stop.customer_code,
          opportunity_id: stop.opportunity_id,
          is_suggested: stop.is_suggested,
          ...stop,
        }));

        // Update both states
        setCalendarEvents(newEvents);
        setSelectedItems(updatedSelectedItems);

        // Navigate calendar to show the first event's date
        if (newEvents.length > 0) {
          const firstEventDate = new Date(newEvents[0].start);
          setCurrentDate(firstEventDate);
        }

        toast({
          title: "Schedule optimized",
          description: `Schedule updated with ${response.data.stops.length} stops over ${numDays} day${numDays > 1 ? 's' : ''}.`,
        });
      } else {
        toast({
          title: "Error",
          description: "Backend did not return valid stops.",
          variant: "destructive",
        });
      }
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Failed to get fill assist.",
        variant: "destructive",
      });
    } finally {
      setFillAssistLoading(false);
    }
  };

  return (
    <div>
      <Header />
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">
            {editItineraryId ? "Edit Your Itinerary" : "Build Your Itinerary"}
          </h1>
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2">
            <Card className="flex flex-col h-full min-h-[calc(100vh-170px)]">
              <CardHeader className="flex flex-row justify-between items-center p-4 border-b">
                <CardTitle className="text-base font-semibold">
                  Customers ({filteredCustomers?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 border-b">
                <div className="space-y-4">
                  {/* Search box */}
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="text"
                      placeholder="Search customers..."
                      className="pl-8"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>

                  {/* Filter dropdown section */}
                  <div className="flex flex-wrap gap-2">
                    {/* Region filter */}
                    {filterOptions.regions &&
                      filterOptions.regions.length > 0 && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex items-center gap-1"
                            >
                              Region
                              <Badge className="ml-1 bg-primary">
                                {selectedFilters.regions.length || 0}
                              </Badge>
                              <ChevronDown className="h-4 w-4 opacity-50" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" className="w-56">
                            {filterOptions.regions.map((region) => (
                              <DropdownMenuCheckboxItem
                                key={region}
                                checked={selectedFilters.regions.includes(
                                  region,
                                )}
                                onCheckedChange={() =>
                                  handleFilterChange("regions", region)
                                }
                              >
                                {region}
                              </DropdownMenuCheckboxItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}

                    {/* State filter */}
                    {filterOptions.states &&
                      filterOptions.states.length > 0 && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex items-center gap-1"
                            >
                              State
                              <Badge className="ml-1 bg-primary">
                                {selectedFilters.states.length || 0}
                              </Badge>
                              <ChevronDown className="h-4 w-4 opacity-50" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="start"
                            className="w-56 max-h-[300px] overflow-y-auto"
                          >
                            {filterOptions.states.map((state) => (
                              <DropdownMenuCheckboxItem
                                key={state}
                                checked={selectedFilters.states.includes(state)}
                                onCheckedChange={() =>
                                  handleFilterChange("states", state)
                                }
                              >
                                {state}
                              </DropdownMenuCheckboxItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}

                    {/* Market filter */}
                    {filterOptions.markets &&
                      filterOptions.markets.length > 0 && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex items-center gap-1"
                            >
                              Market
                              <Badge className="ml-1 bg-primary">
                                {selectedFilters.markets.length || 0}
                              </Badge>
                              <ChevronDown className="h-4 w-4 opacity-50" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" className="w-56">
                            {filterOptions.markets.map((market) => (
                              <DropdownMenuCheckboxItem
                                key={market}
                                checked={selectedFilters.markets.includes(
                                  market,
                                )}
                                onCheckedChange={() =>
                                  handleFilterChange("markets", market)
                                }
                              >
                                {market}
                              </DropdownMenuCheckboxItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}

                    {/* Priority filter */}
                    {filterOptions.priority_buckets &&
                      filterOptions.priority_buckets.length > 0 && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex items-center gap-1"
                            >
                              Priority
                              <Badge className="ml-1 bg-primary">
                                {selectedFilters.priority_buckets.length || 0}
                              </Badge>
                              <ChevronDown className="h-4 w-4 opacity-50" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" className="w-56">
                            {filterOptions.priority_buckets.map((priority) => (
                              <DropdownMenuCheckboxItem
                                key={priority}
                                checked={selectedFilters.priority_buckets.includes(
                                  priority,
                                )}
                                onCheckedChange={() =>
                                  handleFilterChange(
                                    "priority_buckets",
                                    priority,
                                  )
                                }
                              >
                                {priority}
                              </DropdownMenuCheckboxItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}

                    {/* Priority Level filter (High/Medium/Low) */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex items-center gap-1"
                        >
                          Priority
                          <Badge className="ml-1 bg-primary">
                            {selectedFilters.priority_levels.length || 0}
                          </Badge>
                          <ChevronDown className="h-4 w-4 opacity-50" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-56">
                        {["High", "Medium", "Low"].map((level) => (
                          <DropdownMenuCheckboxItem
                            key={level}
                            checked={selectedFilters.priority_levels.includes(
                              level,
                            )}
                            onCheckedChange={() =>
                              handleFilterChange("priority_levels", level)
                            }
                          >
                            <div className="flex items-center gap-2">
                              <Badge
                                variant={getPriorityBadgeVariant(level)}
                                className="h-2 w-2 p-0 rounded-full"
                              />
                              {level}
                            </div>
                          </DropdownMenuCheckboxItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>

                    {/* Clear filters button */}
                    {(selectedFilters.regions.length > 0 ||
                      selectedFilters.states.length > 0 ||
                      selectedFilters.markets.length > 0 ||
                      selectedFilters.priority_buckets.length > 0 ||
                      selectedFilters.priority_levels.length > 0 ||
                      searchQuery) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearFilters}
                        className="text-muted-foreground"
                      >
                        Clear filters
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
              <CardContent className="p-0 flex-1 overflow-hidden h-full relative">
                <div className="flex h-full">
                  {/* Customer List View */}
                  <div
                    className={`transition-all duration-300 ease-in-out ${showOpportunities ? "w-0 opacity-0" : "w-full opacity-100"} overflow-hidden`}
                  >
                    <ScrollArea className="h-[calc(100vh-240px)] md:h-[calc(100vh-270px)]">
                      <div className="divide-y">
                        {loading || customersLoading ? (
                          <div className="p-8 text-center">
                            <div className="flex flex-col items-center gap-4">
                              <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                              <div>
                                <p className="font-medium text-gray-900">
                                  Loading customers...
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  This may take a moment
                                </p>
                              </div>
                            </div>
                          </div>
                        ) : error || customersError ? (
                          <div className="p-8 text-center">
                            <div className="flex flex-col items-center gap-4">
                              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                                <X className="h-6 w-6 text-red-600" />
                              </div>
                              <div>
                                <p className="font-medium text-red-900">
                                  Error loading customers
                                </p>
                                <p className="text-sm text-red-600">
                                  {error || customersError}
                                </p>
                              </div>
                            </div>
                          </div>
                        ) : filteredCustomers &&
                          filteredCustomers.length > 0 ? (
                          <>
                            {paginatedCustomers.map((customer) => {
                              // Use the generated unique ID for selection
                              const uniqueId = customer._uniqueId;

                              // Prepare primary focus display
                              const primaryFocus =
                                customer.primary_focus ||
                                (customer.prioritybucket
                                  ? `Priority: ${customer.prioritybucket}`
                                  : null);

                              return (
                                <div key={uniqueId}>
                                  <div className="px-4 py-3 transition-colors hover:bg-muted/50">
                                    <div className="flex justify-between items-start gap-4">
                                      <div className="flex items-start gap-3 flex-1">
                                        <div className="flex-1">
                                          <div className="font-medium">
                                            {customer.customername ||
                                              "Unnamed Customer"}
                                          </div>
                                          <div className="text-xs text-muted-foreground mt-1">
                                            ID: {customer.customercode || "N/A"}
                                          </div>
                                          {customer.formatted_address && (
                                            <div className="text-xs text-muted-foreground mt-1 flex gap-2">
                                              <MapPin className="h-3 w-3" />
                                              <span>
                                                {customer.formatted_address}
                                              </span>
                                            </div>
                                          )}
                                          {customer.salesregion && (
                                            <div className="text-xs text-muted-foreground mt-1">
                                              Region: {customer.salesregion}
                                            </div>
                                          )}
                                          {primaryFocus && (
                                            <div className="text-xs flex items-center mt-1 gap-1">
                                              <Target className="h-3 w-3 text-muted-foreground" />
                                              <span className="text-muted-foreground">
                                                {primaryFocus}
                                              </span>
                                            </div>
                                          )}
                                          {customer.selected_opportunities &&
                                            customer.selected_opportunities
                                              .length > 0 && (
                                              <div className="mt-1 space-y-1">
                                                <div className="text-xs text-blue-600 font-medium flex items-center gap-1">
                                                  <Target className="h-3 w-3" />
                                                  {
                                                    customer
                                                      .selected_opportunities
                                                      .length
                                                  }{" "}
                                                  {customer
                                                    .selected_opportunities
                                                    .length === 1
                                                    ? "opportunity"
                                                    : "opportunities"}{" "}
                                                  selected
                                                </div>
                                                <div className="flex flex-wrap gap-1">
                                                  {customer.selected_opportunities
                                                    .slice(0, 3)
                                                    .map((opp, idx) => (
                                                      <Badge
                                                        key={idx}
                                                        variant="outline"
                                                        className="text-[10px] px-1.5 py-0.5"
                                                      >
                                                        {opp.city || "Unknown"},{" "}
                                                        {opp.state || "XX"}
                                                      </Badge>
                                                    ))}
                                                  {customer
                                                    .selected_opportunities
                                                    .length > 3 && (
                                                    <Badge
                                                      variant="outline"
                                                      className="text-[10px] px-1.5 py-0.5"
                                                    >
                                                      +
                                                      {customer
                                                        .selected_opportunities
                                                        .length - 3}{" "}
                                                      more
                                                    </Badge>
                                                  )}
                                                </div>
                                              </div>
                                            )}
                                        </div>
                                      </div>
                                      <div className="flex flex-col items-end gap-2 min-w-0">
                                        {customer.prioritybucket && (
                                          <Badge
                                            variant={getPriorityBadgeVariant(
                                              customer.prioritybucket,
                                            )}
                                            className="uppercase text-[10px] whitespace-nowrap"
                                          >
                                            {customer.prioritybucket}
                                          </Badge>
                                        )}
                                        <div className="flex flex-col gap-1.5 w-36">
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-8 px-2 text-xs w-full justify-start"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleOpenCustomerDialog(customer);
                                            }}
                                          >
                                            <Info className="h-3 w-3 mr-1" />
                                            Customer Info
                                          </Button>
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-8 px-2 text-xs w-full justify-start"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleCustomerClickForOpportunities(customer);
                                            }}
                                          >
                                            <Target className="h-3 w-3 mr-1" />
                                            Opportunities
                                          </Button>
                                          <Button
                                            variant={selectedCustomers[uniqueId] ? "secondary" : "default"}
                                            size="sm"
                                            className={`h-8 px-2 text-xs w-full justify-start ${
                                              selectedCustomers[uniqueId] ? "" : "bg-black hover:bg-gray-800 text-white"
                                            }`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleCustomerSelect(uniqueId);
                                            }}
                                          >
                                            {selectedCustomers[uniqueId] ? (
                                              <>
                                                <Check className="h-3 w-3 mr-1" />
                                                Added
                                              </>
                                            ) : (
                                              <>
                                                <UserPlus className="h-3 w-3 mr-1" />
                                                Add to Itinerary
                                              </>
                                            )}
                                          </Button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Customer Details Dialog */}
                                  <Dialog
                                    open={
                                      selectedCustomerForDialog?._uniqueId ===
                                      customer._uniqueId
                                    }
                                    onOpenChange={(open) => {
                                      if (!open) {
                                        setSelectedCustomerForDialog(null);
                                      }
                                    }}
                                  >
                                    <DialogContent className="w-full max-w-[320px] sm:max-w-xl md:max-w-4xl lg:max-w-5xl max-h-[85vh] p-0">
                                      <DialogHeader className="p-3 sm:p-4 border-b bg-muted/30">
                                        <div className="flex items-center justify-between w-full">
                                          <div className="flex items-center gap-2 sm:gap-3">
                                            <div className="flex-shrink-0 h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-base sm:text-lg font-bold">
                                              {(() => {
                                                // Find the customer index in the current page
                                                const customerIndex = filteredCustomers.findIndex(c => c._uniqueId === customer._uniqueId);
                                                return customerIndex >= 0 ? customerIndex + 1 : '?';
                                              })()} 
                                            </div>
                                            <div>
                                              <DialogTitle className="text-xl font-semibold uppercase">
                                                {customer.customername || "Unnamed Customer"}
                                              </DialogTitle>
                                              <DialogDescription className="flex items-center gap-2 text-xs text-muted-foreground">
                                                <span>ID: {customer.customercode || customer.id || "N/A"}</span>
                                                {customer.prioritybucket && (
                                                  <Badge
                                                    variant={getPriorityBadgeVariant(customer.prioritybucket)}
                                                    size="sm"
                                                    className="capitalize"
                                                  >
                                                    {customer.prioritybucket} Priority
                                                  </Badge>
                                                )}
                                                {primaryFocus && (
                                                  <Badge variant="outline" className="ml-1 capitalize text-[10px] px-1 py-0">
                                                    {primaryFocus}
                                                  </Badge>
                                                )}
                                                {/* Enhanced data indicator */}
                                                {(customer.open_quotes?.length > 0 || customer.open_orders?.length > 0 || customer.active_rentals?.length > 0) && (
                                                  <Badge variant="secondary" className="ml-1 text-[10px] px-1 py-0 bg-blue-500/10 text-blue-600">
                                                    Enhanced Data
                                                  </Badge>
                                                )}
                                              </DialogDescription>
                                            </div>
                                          </div>
                                        </div>
                                      </DialogHeader>
                                      <CustomerDialogContent
                                        customer={customer}
                                        isLoading={
                                          customerDetailsLoading[
                                            customer.customercode
                                          ]
                                        }
                                        customerDetailsData={
                                          customerDetailsData
                                        }
                                        customerOpportunitiesData={
                                          customerOpportunitiesData
                                        }
                                        setSelectedCustomerOpportunities={
                                          setSelectedCustomerOpportunities
                                        }
                                        setOpportunitiesDialogOpen={
                                          setOpportunitiesDialogOpen
                                        }
                                      />
                                      {/* Role-based footer buttons */}
                                      {isRSM && (
                                        <div className="p-4 border-t grid grid-cols-2 gap-2">
                                          <Button
                                            onClick={() => {
                                              // Handle RSM status update
                                              toast({
                                                title: "Feature Not Available",
                                                description: "Status update is not available in Build Itinerary mode.",
                                                variant: "destructive",
                                              });
                                            }}
                                          >
                                            Update Status
                                          </Button>
                                          <Button
                                            onClick={() => {
                                              // Handle HubSpot update
                                              toast({
                                                title: "Feature Not Available",
                                                description: "HubSpot update is not available in Build Itinerary mode.",
                                                variant: "destructive",
                                              });
                                            }}
                                            variant="outline"
                                          >
                                            <Projector className="mr-2 h-4 w-4" />
                                            Update HubSpot
                                          </Button>
                                        </div>
                                      )}
                                      {isBM && (
                                        <div className="p-4 border-t">
                                          <Button
                                            onClick={() => {
                                              // Handle BM notes
                                              toast({
                                                title: "Feature Not Available",
                                                description: "Notes feature is not available in Build Itinerary mode.",
                                                variant: "destructive",
                                              });
                                            }}
                                            className="w-full"
                                            variant="outline"
                                          >
                                            {customer.bm_notes ? "Edit Notes" : "Add Notes"}
                                          </Button>
                                        </div>
                                      )}
                                      {/* Main action button */}
                                      <div className="p-4 border-t">
                                        <Button
                                          onClick={() => handleCustomerSelect(uniqueId)}
                                          className="w-full"
                                          variant={selectedCustomers[uniqueId] ? "destructive" : "default"}
                                        >
                                          {selectedCustomers[uniqueId] ? "Remove from Itinerary" : "Add to Itinerary"}
                                        </Button>
                                      </div>
                                    </DialogContent>
                                  </Dialog>
                                </div>
                              );
                            })}

                            {/* Pagination Controls */}
                            {(totalPages > 1 ||
                              (filteredCustomers.length > SHOW_ALL_THRESHOLD &&
                                !showAllResults)) && (
                              <div className="p-4 border-t bg-background/95 backdrop-blur-sm">
                                <div className="flex items-center justify-between">
                                  <div className="text-sm text-muted-foreground">
                                    {showAllResults ||
                                    filteredCustomers.length <=
                                      SHOW_ALL_THRESHOLD ? (
                                      <>
                                        Showing all {filteredCustomers.length}{" "}
                                        customers
                                      </>
                                    ) : (
                                      <>
                                        Showing{" "}
                                        {(currentPage - 1) *
                                          CUSTOMERS_PER_PAGE +
                                          1}{" "}
                                        to{" "}
                                        {Math.min(
                                          currentPage * CUSTOMERS_PER_PAGE,
                                          filteredCustomers.length,
                                        )}{" "}
                                        of {filteredCustomers.length} customers
                                      </>
                                    )}
                                    {filteredCustomers.length <
                                      customers.length && (
                                      <span className="text-blue-600 ml-2">
                                        (
                                        {customers.length -
                                          filteredCustomers.length}{" "}
                                        filtered out)
                                      </span>
                                    )}
                                    {filteredCustomers.length >
                                      SHOW_ALL_THRESHOLD &&
                                      !showAllResults && (
                                        <Button
                                          variant="link"
                                          size="sm"
                                          onClick={() =>
                                            setShowAllResults(true)
                                          }
                                          className="ml-2 h-auto p-0 text-blue-600"
                                        >
                                          Show all {filteredCustomers.length}{" "}
                                          results
                                        </Button>
                                      )}
                                    {showAllResults &&
                                      filteredCustomers.length >
                                        SHOW_ALL_THRESHOLD && (
                                        <Button
                                          variant="link"
                                          size="sm"
                                          onClick={() =>
                                            setShowAllResults(false)
                                          }
                                          className="ml-2 h-auto p-0 text-blue-600"
                                        >
                                          Use pagination
                                        </Button>
                                      )}
                                  </div>
                                  {!showAllResults &&
                                    filteredCustomers.length >
                                      SHOW_ALL_THRESHOLD && (
                                      <div className="flex gap-2">
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => setCurrentPage(1)}
                                          disabled={currentPage === 1}
                                        >
                                          First
                                        </Button>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() =>
                                            setCurrentPage((prev) =>
                                              Math.max(1, prev - 1),
                                            )
                                          }
                                          disabled={currentPage === 1}
                                        >
                                          <ChevronLeft className="h-4 w-4" />
                                        </Button>
                                        <span className="flex items-center px-3 text-sm">
                                          Page {currentPage} of {totalPages}
                                        </span>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() =>
                                            setCurrentPage((prev) =>
                                              Math.min(totalPages, prev + 1),
                                            )
                                          }
                                          disabled={currentPage === totalPages}
                                        >
                                          <ChevronRight className="h-4 w-4" />
                                        </Button>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() =>
                                            setCurrentPage(totalPages)
                                          }
                                          disabled={currentPage === totalPages}
                                        >
                                          Last
                                        </Button>
                                      </div>
                                    )}
                                </div>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="flex flex-col items-center justify-center p-8 h-full min-h-[300px]">
                            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                              <Search className="h-6 w-6 text-gray-400" />
                            </div>
                            <div className="text-center">
                              <p className="font-medium text-gray-900 mb-1">
                                No customers found
                              </p>
                              <p className="text-sm text-muted-foreground mb-4">
                                Try adjusting your search terms or filters
                              </p>
                              {(selectedFilters.regions.length > 0 ||
                                selectedFilters.states.length > 0 ||
                                selectedFilters.markets.length > 0 ||
                                selectedFilters.priority_buckets.length > 0 ||
                                selectedFilters.priority_levels.length > 0 ||
                                searchQuery) && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={clearFilters}
                                >
                                  <X className="h-4 w-4 mr-2" />
                                  Clear all filters
                                </Button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </ScrollArea>
                  </div>

                  {/* Opportunity Selection View */}
                  <div
                    className={`transition-all duration-300 ease-in-out ${showOpportunities ? "w-full opacity-100" : "w-0 opacity-0"} overflow-hidden bg-background border-l`}
                  >
                    {/* Header for Opportunities */}
                    <div className="p-4 border-b bg-muted/30">
                      <div className="flex items-center gap-2 mb-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleBackToCustomerList}
                          className="h-auto p-1"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <div>
                          <h3 className="font-semibold text-base">
                            {selectedCustomerForOpportunities?.customername ||
                              "Customer"}{" "}
                            Opportunities
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            Select opportunities to include in your itinerary
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Opportunities Content */}
                    <ScrollArea className="h-[calc(100vh-340px)] md:h-[calc(100vh-320px)]">
                      {opportunitiesLoading ? (
                        <div className="p-8 text-center">
                          <div className="flex flex-col items-center gap-4">
                            <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                            <div>
                              <p className="font-medium text-gray-900">
                                Loading opportunities...
                              </p>
                              <p className="text-sm text-muted-foreground">
                                Fetching customer data
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : opportunitiesError ? (
                        <div className="p-8 text-center">
                          <div className="flex flex-col items-center gap-4">
                            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                              <X className="h-6 w-6 text-red-600" />
                            </div>
                            <div>
                              <p className="font-medium text-red-900">
                                Error loading opportunities
                              </p>
                              <p className="text-sm text-red-600">
                                {opportunitiesError}
                              </p>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                fetchCustomerOpportunities(
                                  selectedCustomerForOpportunities?.customercode,
                                )
                              }
                            >
                              Retry
                            </Button>
                          </div>
                        </div>
                      ) : customerOpportunities.length === 0 ? (
                        <div className="p-8 text-center">
                          <div className="flex flex-col items-center gap-4">
                            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                              <FileText className="h-6 w-6 text-gray-400" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">
                                No opportunities found
                              </p>
                              <p className="text-sm text-muted-foreground">
                                This customer has no active opportunities
                              </p>
                            </div>
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => {
                                // Add customer without opportunities
                                const customerId =
                                  selectedCustomerForOpportunities._uniqueId;
                                setSelectedCustomers((prev) => ({
                                  ...prev,
                                  [customerId]: true,
                                }));
                                toast({
                                  title: "Added to itinerary",
                                  description: `Added ${selectedCustomerForOpportunities.customername} to your itinerary`,
                                });
                                handleBackToCustomerList();
                              }}
                            >
                              Add Customer Anyway
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="divide-y">
                          {/* Opportunities List */}
                          <div className="p-4">
                            <div className="flex items-center justify-between mb-3">
                              <div className="text-sm text-muted-foreground">
                                {(() => {
                                  const filteredCount =
                                    customerOpportunities.filter((opp) => {
                                      const hasCoords =
                                        opp.coordinates &&
                                        opp.coordinates.lat &&
                                        opp.coordinates.lng;
                                      return (
                                        showNullCoordOpportunities || hasCoords
                                      );
                                    }).length;
                                  const noGpsCount =
                                    customerOpportunities.filter(
                                      (opp) =>
                                        !opp.coordinates ||
                                        !opp.coordinates.lat ||
                                        !opp.coordinates.lng,
                                    ).length;

                                  return (
                                    <>
                                      {selectedOpportunityIds.length} of{" "}
                                      {filteredCount} selected
                                      {!showNullCoordOpportunities &&
                                        noGpsCount > 0 && (
                                          <span className="text-muted-foreground/70">
                                            {" "}
                                            ({noGpsCount} hidden)
                                          </span>
                                        )}
                                    </>
                                  );
                                })()}
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  setShowNullCoordOpportunities(
                                    !showNullCoordOpportunities,
                                  )
                                }
                                className="flex items-center gap-2"
                              >
                                <MapPin className="h-4 w-4" />
                                {showNullCoordOpportunities
                                  ? "Hide"
                                  : "Show"}{" "}
                                No-GPS Opps
                              </Button>
                            </div>

                            <div className="space-y-2">
                              {customerOpportunities
                                .filter((opportunity) => {
                                  // Filter based on coordinates
                                  const hasCoords =
                                    opportunity.coordinates &&
                                    opportunity.coordinates.lat &&
                                    opportunity.coordinates.lng;
                                  return (
                                    showNullCoordOpportunities || hasCoords
                                  );
                                })
                                .map((opportunity, index) => {
                                  // Generate unique ID combining type, location, and index to handle duplicate backend IDs
                                  const opportunityId = `${opportunity.type || "unknown"}-${opportunity.projectcity || "unknown"}-${opportunity.projectstate || "unknown"}-${index}`;
                                  const isSelected =
                                    selectedOpportunityIds.includes(
                                      opportunityId,
                                    );
                                  const hasCoords =
                                    opportunity.coordinates &&
                                    opportunity.coordinates.lat &&
                                    opportunity.coordinates.lng;

                                  return (
                                    <div
                                      key={opportunityId}
                                      className={`p-3 border rounded-lg cursor-pointer transition-all hover:shadow-sm ${
                                        isSelected
                                          ? "border-primary bg-primary/5"
                                          : "border-border hover:border-primary/50"
                                      }`}
                                      onClick={() =>
                                        handleOpportunitySelection(
                                          opportunityId,
                                          !isSelected,
                                        )
                                      }
                                    >
                                      <div className="flex items-start gap-3">
                                        <Checkbox
                                          checked={isSelected}
                                          onChange={() =>
                                            handleOpportunitySelection(
                                              opportunityId,
                                              !isSelected,
                                            )
                                          }
                                          className="mt-1"
                                          onClick={(e) => e.stopPropagation()}
                                        />

                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center justify-between gap-2 mb-1">
                                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                              <h4 className="font-medium text-sm truncate">
                                                {opportunity.type === "quote"
                                                  ? "Quote"
                                                  : opportunity.type === "order"
                                                    ? "Order"
                                                    : "Opportunity"}{" "}
                                                - {opportunity.projectcity},{" "}
                                                {opportunity.projectstate}
                                              </h4>
                                              {!hasCoords && (
                                                <Badge
                                                  variant="outline"
                                                  className="text-[10px] px-1.5 py-0.5 flex items-center gap-1"
                                                >
                                                  <MapPin className="h-3 w-3" />
                                                  No GPS
                                                </Badge>
                                              )}
                                            </div>
                                            {(opportunity.estimatedvalue ||
                                              opportunity.amount) && (
                                              <div className="text-sm font-semibold text-green-600">
                                                {new Intl.NumberFormat(
                                                  "en-US",
                                                  {
                                                    style: "currency",
                                                    currency: "USD",
                                                    notation: "compact",
                                                    minimumFractionDigits: 0,
                                                    maximumFractionDigits: 0,
                                                  },
                                                ).format(
                                                  opportunity.estimatedvalue ||
                                                    opportunity.amount ||
                                                    0,
                                                )}
                                              </div>
                                            )}
                                          </div>

                                          <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-xs text-muted-foreground">
                                            {opportunity.type && (
                                              <>
                                                <span>Type:</span>
                                                <span className="capitalize">
                                                  {opportunity.type}
                                                </span>
                                              </>
                                            )}
                                            {opportunity.markettype && (
                                              <>
                                                <span>Market:</span>
                                                <span>
                                                  {opportunity.markettype}
                                                </span>
                                              </>
                                            )}
                                            {opportunity.projectcity &&
                                              opportunity.projectstate && (
                                                <>
                                                  <span>Location:</span>
                                                  <span>
                                                    {opportunity.projectcity},{" "}
                                                    {opportunity.projectstate}
                                                  </span>
                                                </>
                                              )}
                                            {opportunity.lastupdate && (
                                              <>
                                                <span>Updated:</span>
                                                <span>
                                                  {new Date(
                                                    opportunity.lastupdate,
                                                  ).toLocaleDateString()}
                                                </span>
                                              </>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        </div>
                      )}
                    </ScrollArea>

                    {/* Footer with Actions */}
                    {customerOpportunities.length > 0 && (
                      <div className="absolute bottom-0 left-0 right-0 p-4 border-t bg-background/95 backdrop-blur-sm">
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            onClick={handleBackToCustomerList}
                            className="flex-1"
                          >
                            Cancel
                          </Button>
                          <Button
                            onClick={handleAddOpportunitiesToItinerary}
                            disabled={selectedOpportunityIds.length === 0}
                            className="flex-1"
                          >
                            Add to Itinerary ({selectedOpportunityIds.length})
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-3">
            {hasSelectedCustomers ? (
              <div className="space-y-4 min-h-[calc(100vh-170px)] flex flex-col">
                {/* Calendar Card */}
                <Card className="overflow-hidden flex-grow">
                  <CardHeader className="flex flex-row justify-between items-center p-4 border-b">
                    <CardTitle className="text-base font-semibold">
                      Itinerary Calendar
                    </CardTitle>
                    <Badge
                      variant="outline"
                      className="flex items-center gap-1"
                    >
                      <Clock className="h-3 w-3" />
                      {totalStopsCount}{" "}
                      {totalStopsCount === 1 ? "stop" : "stops"}
                      {totalStopsCount !== selectedCustomerCount &&
                        ` (${selectedCustomerCount} customers)`}
                    </Badge>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="h-[600px] relative">
                      {(() => {
                        console.log("Calendar render check:", {
                          hasLocalizer: !!localizer,
                          hasFormats: !!localizer?.formats,
                          eventsLength: calendarEvents?.length || 0,
                          currentDate: currentDate,
                        });
                        return (
                          isValidDate(currentDate) &&
                          Array.isArray(calendarEvents) &&
                          localizer &&
                          localizer.formats
                        );
                      })() ? (
                        <SafeCalendar
                          key={`calendar-${calendarEvents.length}-${calendarEvents.map(e => e.id).join('-')}`}
                          localizer={localizer}
                          formats={calendarFormats}
                          events={
                            calendarEvents.length > 0 ? calendarEvents : []
                          }
                          startAccessor="start"
                          endAccessor="end"
                          defaultView="week"
                          views={["week", "day"]}
                          step={30}
                          timeslots={2}
                          showMultiDayTimes
                          popup
                          doShowMoreDrillDown
                          eventPropGetter={eventStyleGetter}
                          min={timeSettings.minTime}
                          max={timeSettings.maxTime}
                          scrollToTime={scrollToTimeValue}
                          onNavigate={handleCalendarNavigate}
                          date={currentDate}
                          getNow={() => new Date()}
                          components={{
                            toolbar: (toolbarProps) => (
                              <CustomToolbar
                                {...toolbarProps}
                                isDragging={isDragging}
                              />
                            ),
                            event: CustomEvent,
                          }}
                          style={calendarStyles.style}
                          selectable
                          onEventDrop={handleEnhancedEventDrop}
                          resizableAccessor={resizableAccessor}
                          onEventResize={handleEventResize}
                          dragFromOutsideItem={null}
                          onDropFromOutside={null}
                          onDragStart={handleDragStart}
                          onDragEnd={handleDragEnd}
                          onDragOver={(e) => {
                            // Allow cross-week dropping
                            e.preventDefault();
                            return true;
                          }}
                          className={calendarStyles.className}
                          dayPropGetter={calendarStyles.dayPropGetter}
                        />
                      ) : (
                        <div className="h-full flex items-center justify-center bg-muted/30 rounded-md border">
                          <div className="text-center p-4">
                            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-muted-foreground" />
                            <p className="text-muted-foreground">
                              Loading calendar...
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                  <CardFooter className="p-4 border-t">
                    <div className="w-full flex flex-col sm:flex-row gap-2 justify-between">
                      {/* Schedule Fill Assist Section */}
                      <div className="flex flex-col sm:flex-row gap-2 w-full">
                        <div className="flex items-center gap-2">
                          <label
                            htmlFor="num-days"
                            className="text-sm font-medium whitespace-nowrap"
                          >
                            Days:
                          </label>
                          <Input
                            id="num-days"
                            type="number"
                            min={1}
                            max={30}
                            value={numDays}
                            onChange={(e) =>
                              setNumDays(
                                Math.max(
                                  1,
                                  Math.min(30, Number(e.target.value)),
                                ),
                              )
                            }
                            className="w-20"
                          />
                        </div>
                        <Button
                          variant="secondary"
                          className="whitespace-nowrap"
                          disabled={
                            loading ||
                            calendarEvents.length === 0 ||
                            fillAssistLoading
                          }
                          onClick={handleScheduleFillAssist}
                        >
                          {fillAssistLoading ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent mr-2" />
                              Optimizing...
                            </>
                          ) : (
                            "Schedule Fill Assist"
                          )}
                        </Button>
                      </div>
                      {/* Save/Export Buttons */}
                      <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                        <Button
                          className={`w-full sm:flex-1 ${itinerarySaved ? "bg-green-600 hover:bg-green-700 text-white" : ""}`}
                          onClick={() => {
                            console.log("Save button clicked");
                            console.log("Loading state:", loading);
                            console.log("Itinerary saved state:", itinerarySaved);
                            handleSaveItinerary();
                          }}
                          disabled={loading || itinerarySaved}
                        >
                          {itinerarySaved
                            ? "Itinerary Saved ✓"
                            : loading
                              ? "Saving..."
                              : "Save Itinerary"}
                        </Button>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={handleExportICS}
                          disabled={calendarEvents.length === 0}
                          title="Export schedule as .ics file"
                          className="w-full sm:w-auto"
                          style={{
                            backgroundColor: "#0078D4",
                            borderColor: "#0078D4",
                            color: "#FFFFFF",
                          }}
                        >
                          <CalendarIcon className="mr-1.5 h-4 w-4" />
                          Export to Calendar
                        </Button>
                      </div>
                    </div>
                  </CardFooter>
                </Card>

                {/* Map for Selected Customers */}
                <Card className="overflow-hidden h-[250px] lg:h-[300px]">
                  <CardContent className="p-0 h-full">
                    <MapSection
                      key={`map-${calendarEvents.map((e) => `${e.id}-${e.start.getTime()}`).join("-")}`}
                      customers={selectedCustomersForMap}
                      directionsRequest={selectedCustomersDirectionsRequest}
                    />
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card>
                <CardContent className="p-6 text-center">
                  <Bookmark className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                  <Text className="text-lg font-medium">
                    No Custom Itinerary Built
                  </Text>
                  <Text className="text-muted-foreground mt-1">
                    {Object.keys(selectedCustomers).length === 0
                      ? "Select customers by clicking the checkboxes"
                      : totalStopsCount === selectedCustomerCount
                        ? `${selectedCustomerCount} customers selected`
                        : `${selectedCustomerCount} customers selected (${totalStopsCount} total stops)`}
                  </Text>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
      {/* Save Itinerary Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Save Itinerary</DialogTitle>
            <DialogDescription>
              Give your custom itinerary a name and description.
              {totalStopsCount === selectedCustomerCount
                ? ` This itinerary contains ${selectedCustomerCount} customers.`
                : ` This itinerary contains ${selectedCustomerCount} customers with ${totalStopsCount} total stops.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="itinerary-name" className="text-sm font-medium">
                Name <span className="text-destructive">*</span>
              </label>
              <Input
                id="itinerary-name"
                value={itineraryName}
                onChange={(e) => setItineraryName(e.target.value)}
                placeholder="Enter itinerary name"
                className="w-full"
              />
            </div>
            <div className="space-y-2">
              <label
                htmlFor="itinerary-description"
                className="text-sm font-medium"
              >
                Description
              </label>
              <Textarea
                id="itinerary-description"
                value={itineraryDescription}
                onChange={(e) => setItineraryDescription(e.target.value)}
                placeholder="Enter a brief description (optional)"
                className="w-full"
                rows={3}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setShowSaveDialog(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmSave}
              disabled={loading || !itineraryName.trim()}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Itinerary"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Comprehensive Opportunities Dialog */}
      <Dialog
        open={opportunitiesDialogOpen && selectedCustomerOpportunities}
        onOpenChange={(open) => {
          if (!open) {
            setOpportunitiesDialogOpen(false);
            setOpportunitiesFilter('all');
            setOpportunitiesSort('amount_desc');
            setOpportunitiesSearch('');
          }
        }}
      >
        <DialogContent className="max-w-6xl w-[95vw] h-[85vh] p-0">
          <DialogHeader className="p-4 border-b bg-muted/30">
            <DialogTitle className="text-xl font-semibold">
              {selectedCustomerOpportunities?.customer?.name || "Customer"} - All Opportunities
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              View all quotes, orders, and rentals for this customer. Use filters to find specific opportunities.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 flex flex-col min-h-0">
            {/* Filter Controls */}
            <div className="border-b bg-muted/20 p-4 space-y-4">
              <div className="flex flex-col sm:flex-row gap-4">
                {/* Type Filter */}
                <div className="flex-1">
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                    Filter by Type
                  </label>
                  <select
                    value={opportunitiesFilter}
                    onChange={(e) => setOpportunitiesFilter(e.target.value)}
                    className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="all">All Types</option>
                    <option value="quotes">Quotes Only</option>
                    <option value="orders">Orders Only</option>
                    <option value="rentals">Rentals Only</option>
                  </select>
                </div>

                {/* Sort */}
                <div className="flex-1">
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                    Sort by
                  </label>
                  <select
                    value={opportunitiesSort}
                    onChange={(e) => setOpportunitiesSort(e.target.value)}
                    className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="amount_desc">Amount (High to Low)</option>
                    <option value="amount_asc">Amount (Low to High)</option>
                    <option value="name_asc">Name (A to Z)</option>
                    <option value="date_desc">Date (Newest First)</option>
                  </select>
                </div>

                {/* Search */}
                <div className="flex-1">
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                    Search
                  </label>
                  <input
                    type="text"
                    placeholder="Search opportunities..."
                    value={opportunitiesSearch}
                    onChange={(e) => setOpportunitiesSearch(e.target.value)}
                    className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                  />
                </div>
              </div>

              {/* Map Filter Toggle */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="mappable-filter"
                  checked={showOnlyMappable}
                  onChange={(e) => setShowOnlyMappable(e.target.checked)}
                  className="rounded border-input"
                />
                <label htmlFor="mappable-filter" className="text-sm font-medium">
                  Show only opportunities with locations
                </label>
              </div>
            </div>

            {/* Opportunities List */}
            <ScrollArea className="flex-1">
              <div className="p-4">
                {(() => {
                  // Get opportunities and apply filters
                  const opportunities = selectedCustomerOpportunities?.opportunities || [];
                  
                  // Filter by type
                  let filteredOpportunities = opportunities;
                  if (opportunitiesFilter !== 'all') {
                    filteredOpportunities = opportunities.filter(opp => opp.type === opportunitiesFilter.replace('s', ''));
                  }

                  // Filter by mappable
                  if (showOnlyMappable) {
                    filteredOpportunities = filteredOpportunities.filter(opp => 
                      opp.coordinates && opp.coordinates.lat && opp.coordinates.lng
                    );
                  }

                  // Filter by search
                  if (opportunitiesSearch) {
                    const searchLower = opportunitiesSearch.toLowerCase();
                    filteredOpportunities = filteredOpportunities.filter(opp =>
                      (opp.opportunityname || '').toLowerCase().includes(searchLower) ||
                      (opp.opportunitynum || '').toLowerCase().includes(searchLower) ||
                      (opp.ordernumber || '').toLowerCase().includes(searchLower)
                    );
                  }

                  // Sort opportunities
                  filteredOpportunities.sort((a, b) => {
                    switch (opportunitiesSort) {
                      case 'amount_desc':
                        return (b.estimatedvalue || 0) - (a.estimatedvalue || 0);
                      case 'amount_asc':
                        return (a.estimatedvalue || 0) - (b.estimatedvalue || 0);
                      case 'name_asc':
                        return (a.opportunityname || '').localeCompare(b.opportunityname || '');
                      case 'date_desc':
                        return new Date(b.datemodified || 0) - new Date(a.datemodified || 0);
                      default:
                        return 0;
                    }
                  });

                  if (filteredOpportunities.length === 0) {
                    return (
                      <div className="text-center py-12 text-muted-foreground">
                        <Target className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p className="text-lg font-medium">No opportunities found</p>
                        <p className="text-sm">Try adjusting your filters or search terms</p>
                      </div>
                    );
                  }

                  return (
                    <div className="grid gap-3">
                      {filteredOpportunities.map((opportunity, idx) => (
                        <Card key={idx} className="hover:shadow-md transition-shadow">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-2">
                                  {opportunity.type === 'quote' && <FileText className="h-4 w-4 text-blue-600" />}
                                  {opportunity.type === 'order' && <CheckCircle2 className="h-4 w-4 text-green-600" />}
                                  {opportunity.type === 'rental' && <Heart className="h-4 w-4 text-purple-600" />}
                                  <Badge variant="outline" className="text-xs capitalize">
                                    {opportunity.type}
                                  </Badge>
                                  {opportunity.markettype && (
                                    <Badge variant="secondary" className="text-xs">
                                      {opportunity.markettype}
                                    </Badge>
                                  )}
                                </div>
                                
                                <h4 className="font-medium text-sm truncate mb-1">
                                  {opportunity.opportunityname || "Unnamed Opportunity"}
                                </h4>
                                
                                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                  <span>#{opportunity.opportunitynum || opportunity.ordernumber}</span>
                                  {opportunity.datemodified && (
                                    <span>{formatDate(opportunity.datemodified)}</span>
                                  )}
                                </div>

                                {/* Address Information */}
                                {(opportunity.address || opportunity.city || opportunity.projectcity) && (
                                  <div className="mt-3 pt-3 border-t">
                                    <p className="text-xs font-medium text-muted-foreground mb-2">
                                      Location
                                    </p>
                                    <div className="flex items-start gap-2">
                                      <MapPin className="w-3 h-3 text-muted-foreground mt-0.5 flex-shrink-0" />
                                      <div className="flex-1">
                                        {opportunity.address && (
                                          <p className="text-xs text-foreground mb-1">
                                            {opportunity.address}
                                          </p>
                                        )}
                                        <p className="text-xs text-muted-foreground">
                                          {[
                                            opportunity.city || opportunity.projectcity,
                                            opportunity.state || opportunity.projectstate,
                                            opportunity.zipcode
                                          ].filter(Boolean).join(', ')}
                                        </p>
                                        {opportunity.markettype && (
                                          <p className="text-xs text-muted-foreground mt-1">
                                            Market: {opportunity.markettype}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                              
                              <div className="text-right">
                                <div className="text-lg font-bold">
                                  ${(opportunity.estimatedvalue || 0).toLocaleString()}
                                </div>
                                <div className="flex items-center gap-1 mt-1">
                                  {opportunity.coordinates && (
                                    <Button
                                      variant="default"
                                      size="sm"
                                      className="h-6 px-2 text-xs bg-black hover:bg-gray-800 text-white"
                                      onClick={() => {
                                        setSelectedMapOpportunity(opportunity);
                                        setMapPopupOpen(true);
                                      }}
                                    >
                                      <Map className="h-3 w-3 mr-1" />
                                      View on Map
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>

      {/* Map Popup Dialog */}
      <Dialog open={mapPopupOpen} onOpenChange={(open) => {
        setMapPopupOpen(open);
        if (!open) {
          // Small delay to allow map cleanup
          setTimeout(() => {
            setSelectedMapOpportunity(null);
          }, 100);
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>
              {selectedMapOpportunity?.opportunityname || selectedMapOpportunity?.ordernumber || "Opportunity"} - Location Map
            </DialogTitle>
          </DialogHeader>
          
          {selectedMapOpportunity && mapPopupOpen && (
            <div className="space-y-4">
              {/* Opportunity Info Bar */}
              <div className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg">
                <Badge variant="outline" className={`text-xs font-medium ${
                  selectedMapOpportunity.type === 'quote' ? 'border-blue-500 text-blue-700' :
                  selectedMapOpportunity.type === 'order' ? 'border-green-500 text-green-700' :
                  'border-purple-500 text-purple-700'
                }`}>
                  {selectedMapOpportunity.type?.toUpperCase()}
                </Badge>
                <div className="flex-1">
                  <p className="font-medium">{selectedMapOpportunity.opportunityname || selectedMapOpportunity.opportunitynum}</p>
                  <p className="text-sm text-muted-foreground">
                    {selectedMapOpportunity.address ? selectedMapOpportunity.address + ', ' : ''}
                    {[
                      selectedMapOpportunity.city || selectedMapOpportunity.projectcity,
                      selectedMapOpportunity.state || selectedMapOpportunity.projectstate,
                      selectedMapOpportunity.zipcode
                    ].filter(Boolean).join(', ')}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-bold">${(selectedMapOpportunity.estimatedvalue || 0).toLocaleString()}</p>
                  {selectedMapOpportunity.coordinates && (
                    <p className="text-xs text-muted-foreground font-mono">
                      {selectedMapOpportunity.coordinates.lat?.toFixed(4)}, {selectedMapOpportunity.coordinates.lng?.toFixed(4)}
                    </p>
                  )}
                </div>
              </div>
              
              {/* Map Container */}
              <div className="h-96 rounded-lg overflow-hidden border">
                {(selectedMapOpportunity.coordinates?.lat && selectedMapOpportunity.coordinates?.lng) ? (
                  <MapContainer
                    key={`map-${selectedMapOpportunity.opportunitynum}-${selectedMapOpportunity.type}`}
                    center={[selectedMapOpportunity.coordinates.lat, selectedMapOpportunity.coordinates.lng]}
                    zoom={15}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    />
                    <Marker 
                      position={[selectedMapOpportunity.coordinates.lat, selectedMapOpportunity.coordinates.lng]}
                      icon={L.divIcon({
                        className: 'custom-div-icon',
                        html: `<div style="background-color: ${
                          selectedMapOpportunity.type === 'quote' ? '#3b82f6' :
                          selectedMapOpportunity.type === 'order' ? '#10b981' : '#8b5cf6'
                        }; width: 20px; height: 20px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
                        iconSize: [20, 20],
                        iconAnchor: [10, 10]
                      })}
                    >
                      <LeafletPopup>
                        <div className="p-2">
                          <h3 className="font-semibold">{selectedMapOpportunity.opportunityname || selectedMapOpportunity.opportunitynum}</h3>
                          <p className="text-sm text-muted-foreground">#{selectedMapOpportunity.opportunitynum}</p>
                          <p className="text-sm">
                            {selectedMapOpportunity.address ? selectedMapOpportunity.address + ', ' : ''}
                            {[
                              selectedMapOpportunity.city || selectedMapOpportunity.projectcity,
                              selectedMapOpportunity.state || selectedMapOpportunity.projectstate
                            ].filter(Boolean).join(', ')}
                          </p>
                          <p className="font-medium">${(selectedMapOpportunity.estimatedvalue || 0).toLocaleString()}</p>
                        </div>
                      </LeafletPopup>
                    </Marker>
                  </MapContainer>
                ) : (
                  <div className="flex items-center justify-center h-full bg-muted/20">
                    <p className="text-muted-foreground">No location coordinates available</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default BuildItineraryPage;
